import { Fragment, useEffect, useRef, useState } from 'react'
import SideBar from '../Sidebar/SideBar'
import { Icon } from '@iconify/react/dist/iconify.js';
import ModelValidityExtend from '../Models/ModelValidityExtend';

import Branches from '../../Api/Branches';
import RegisterClients from '../../Api/RegisterClients';
import BusinessType from '../../Api/BusinessType';
import Package from '../../Api/Package';
import AccountManagerInformationApi from '../../Api/AccountManagerInformationApi';
import AccountManager from '../../Api/AccountManager';
import Swal from "sweetalert2";
import Loader from '../Loader';
import SearchableDropdown from './SearchableDropdown';
import ModelssueRemarkList from '../Models/ModelssueRemarkList';
import NotificationPopupApi from '../../Api/NotificationPopup';
import JoditEditor from 'jodit-react';
import Auth from '../../Api/Auth';
import CouponMaster from '../../Api/CouponMaster';
import PriceModifier from '../../Api/PriceModifier';

const PAGE_LIMIT = 100;

// ── Multi-select dropdown component ──────────────────────────────────────────
const MultiSelectDropdown = ({ options, value, onChange, placeholder = "----Select----" }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const getOptId = (opt) => String(opt.Id ?? opt.id ?? opt.ID ?? "");

    // value is comma-separated string e.g. "1,3,5"
    const selected = value ? value.split(",").filter(Boolean) : [];

    const toggle = (optId) => {
        const strId = String(optId);
        const isSelected = selected.includes(strId);
        const next = isSelected
            ? selected.filter((v) => v !== strId)
            : [...selected, strId];
        onChange(next.join(","));
    };

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const displayLabel =
        selected.length === 0
            ? placeholder
            : options
                .filter((o) => selected.includes(getOptId(o)))
                .map((o) => o.name)
                .join(", ");

    return (
        <div ref={ref} style={{ position: "relative", width: "100%" }}>
            {/* Trigger */}
            <div
                className="modify_im_input mode_category1"
                onClick={() => setOpen((prev) => !prev)}
                style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    userSelect: "none",
                    overflow: "hidden",
                }}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {displayLabel}
                </span>
                <Icon
                    icon={open ? "lucide:chevron-up" : "lucide:chevron-down"}
                    style={{ flexShrink: 0, marginLeft: 4 }}
                />
            </div>

            {/* Dropdown */}
            {open && (
                <div
                    style={{
                        position: "absolute",
                        top: "calc(100% + 2px)",
                        left: 0,
                        right: 0,
                        zIndex: 99999,
                        background: "#ffffff",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        maxHeight: 220,
                        overflowY: "auto",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                >
                    {options.length === 0 ? (
                        <div style={{ padding: "8px 12px", color: "#888", fontSize: 13 }}>No options</div>
                    ) : (
                        options.map((opt) => {
                            const optId = getOptId(opt);
                            const isChecked = selected.includes(optId);

                            return (
                                <div
                                    key={optId}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggle(optId);
                                    }}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        fontSize: 14,
                                        color: "#333",
                                        backgroundColor: isChecked ? "#f0f4ff" : "#ffffff",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = isChecked ? "#e8eeff" : "#f5f5f5";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = isChecked ? "#f0f4ff" : "#ffffff";
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggle(optId)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ width: 15, height: 15, accentColor: "#4f46e5", flexShrink: 0 }}
                                    />
                                    <span>{opt.name}</span>
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

const ClientList = () => {
    const [branchData, setBranchData] = useState([]);
    const [registeredClient, setRegisteredClient] = useState([]);
    const [firmF3Values, setFirmF3Values] = useState({});
    const [savingF3Ids, setSavingF3Ids] = useState({});
    const [businessData, setBusinessData] = useState([]);
    const [packageData, setPackageData] = useState([]);
    const [accountManagerInformationData, setAccountManagerInformationData] = useState([]);
    const [accountManagerMasterData, setAccountManagerMasterData] = useState([]);
    const [isModalOpenValidity, setIsModalOpenValidity] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    // const [selectedDropdown, setSelectedDropdown] = useState({ firmName: null, branchIndex: null });
    const [openDropdowns, setOpenDropdowns] = useState({});
    const [clientSelectedOptions, setClientSelectedOptions] = useState({});
    const [searchCriteria, setSearchCriteria] = useState('All');
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageNo, setPageNo] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [copiedTokenRowId, setCopiedTokenRowId] = useState(null);
    const copyTooltipTimerRef = useRef(null);
    const [isBulkUpdateModalOpen, setIsBulkUpdateModalOpen] = useState(false);
    const [bulkUpdateField, setBulkUpdateField] = useState('universalLink');
    const [bulkUpdateText, setBulkUpdateText] = useState('');
    const [selectedBranchIds, setSelectedBranchIds] = useState([]);
    const [showGenerateTokenForAll, setShowGenerateTokenForAll] = useState(false);
    const [collapsedFirms, setCollapsedFirms] = useState({});
    const [installationDateEdits, setInstallationDateEdits] = useState({});
    const [savingInstallationIds, setSavingInstallationIds] = useState({});
    const [bulkAccountManagerId, setBulkAccountManagerId] = useState('');
    const [firmAccountManagerEdits, setFirmAccountManagerEdits] = useState({});
    const [savingFirmAccountManager, setSavingFirmAccountManager] = useState({});
    const [firmInstallationEdits, setFirmInstallationEdits] = useState({});
    const [firmExpiryEdits, setFirmExpiryEdits] = useState({});
    const [savingFirmInstallation, setSavingFirmInstallation] = useState({});
    const [savingFirmExpiry, setSavingFirmExpiry] = useState({});
    const [savingFirmStatus, setSavingFirmStatus] = useState({});
    const [scheduleData, setScheduleData] = useState({
        fromDate: '',
        toDate: '',
        startTime: '',
        endTime: ''
    });

    console.log("packageData", packageData);

    useEffect(() => {
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const currentDate = now.toISOString().split('T')[0];
        const tomorrowDate = tomorrow.toISOString().split('T')[0];
        const currentTime = now.toTimeString().slice(0, 5);

        setScheduleData({
            fromDate: currentDate,
            toDate: tomorrowDate,
            startTime: currentTime,
            endTime: "23:59"
        });
    }, []);


    useEffect(() => {
        console.log("branchData", branchData);
        console.log("registeredClient", registeredClient);
        console.log("accountManagerInformationData", accountManagerInformationData);
        console.log("accountManagerMasterData", accountManagerMasterData);
    }, [registeredClient, branchData, accountManagerInformationData, accountManagerMasterData]);


    const fetchClientData = async (currentPage = pageNo, criteria = searchCriteria, text = searchText) => {
        setLoading(true);
        const response = await Branches.GetAllClients(currentPage, PAGE_LIMIT, criteria, text)
        if (response) {
            console.log("response", response);
            setBranchData(response.Result || [])
            setTotalPages(response.TotalPages || 1);
        }
        setLoading(false);
        return response.Result;
    }

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 400);

        return () => clearTimeout(debounceTimer);
    }, [searchText]);

    useEffect(() => {
        fetchClientData(pageNo, searchCriteria, debouncedSearchText)
    }, [pageNo, searchCriteria, debouncedSearchText]);

    // useEffect(() => {
    //     setSelectedBranchIds([]);
    // }, [searchCriteria, searchText]);

    const fetchRegisteredClients = async (currentPage = pageNo, criteria = searchCriteria, text = searchText) => {
        setLoading(true);
        const response = await RegisterClients.GetAllRegisteredClients(currentPage, PAGE_LIMIT, criteria, text);
        if (response) {
            console.log("response", response);
            setRegisteredClient(response.Result || [])
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchRegisteredClients(pageNo, searchCriteria, debouncedSearchText)
    }, [pageNo, searchCriteria, debouncedSearchText]);


    const fetchBusinessData = async () => {
        setLoading(true);
        const response = await BusinessType.GetAllBusinessType()
        if (response) {
            console.log("response", response);
            setBusinessData(response.Result)
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchBusinessData()
    }, []);

    const toggleDropdown = (clientId) => {
        setOpenDropdowns((prevState) => ({
            ...prevState,
            [clientId]: !prevState[clientId], // Toggle the dropdown for the specific clientId
        }));
    };

    const closeAllDropdowns = (e) => {
        e.preventDefault();
        setOpenDropdowns({});
    }

    const getRegisterClientForBranch = (client) => {
        const nested = client?.registerClient;
        const branchClientId = nested?.id ?? client?.clientId;
        if (branchClientId == null || branchClientId === '') {
            return nested;
        }

        const fromList = registeredClient.find(
            (regClient) => String(regClient.id) === String(branchClientId)
        );

        if (nested && fromList) {
            return {
                ...fromList,
                ...nested,
                Email: nested.Email ?? fromList.Email,
                F3: nested.F3 ?? fromList.F3,
            };
        }

        return nested || fromList;
    };

    const handleSaveFirmF3 = async (e, firmRegisterClient) => {
        e.preventDefault();
        e.stopPropagation();

        const registerClientId = firmRegisterClient?.id;
        if (registerClientId == null) {
            return;
        }

        const f3Value = firmF3Values[registerClientId] ?? firmRegisterClient?.F3 ?? '';
        setSavingF3Ids((prev) => ({ ...prev, [registerClientId]: true }));

        try {
            const response = await RegisterClients.UpdateF3(registerClientId, f3Value);
            if (response?.IsSuccess || response?.StatusCode === 200) {
                setRegisteredClient((prev) =>
                    prev.map((item) =>
                        String(item.id) === String(registerClientId)
                            ? { ...item, F3: f3Value }
                            : item
                    )
                );
                setBranchData((prev) =>
                    prev.map((item) =>
                        String(item.registerClient?.id) === String(registerClientId)
                            ? { ...item, registerClient: { ...item.registerClient, F3: f3Value } }
                            : item
                    )
                );
                setFirmF3Values((prev) => ({ ...prev, [registerClientId]: f3Value }));
                Swal.fire({
                    title: "Updated",
                    text: "Value saved successfully.",
                    icon: "success",
                    width: "320px"
                });
            } else {
                Swal.fire({
                    title: "Error",
                    text: response?.ErrorMessages || "Failed to save value.",
                    icon: "error",
                    width: "320px"
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                title: "Error",
                text: "Failed to save value.",
                icon: "error",
                width: "320px"
            });
        } finally {
            setSavingF3Ids((prev) => ({ ...prev, [registerClientId]: false }));
        }
    };

    const groupedClients = branchData.reduce((acc, client) => {
        const registerClientdata = getRegisterClientForBranch(client);
        const firmName = (registerClientdata?.FirmName || '').trim() || 'Firm Name Not Found';

        if (!acc[firmName]) {
            acc[firmName] = [];
        }
        acc[firmName].push(client);
        return acc;
    }, {});

    useEffect(() => {
        setClientSelectedOptions((prevSelectedOptions) => {
            const nextSelectedOptions = { ...prevSelectedOptions };
            let hasChanges = false;

            Object.keys(groupedClients).forEach((firmName) => {
                groupedClients[firmName].forEach((client) => {
                    if (nextSelectedOptions[client.id] !== undefined) {
                        return;
                    }

                    if (client.businessType) {
                        const rawValues = String(client.businessType)
                            .split(",")
                            .map((item) => item.trim())
                            .filter(Boolean);

                        const businessIds = rawValues
                            .map((value) => {
                                if (/^\d+$/.test(value)) {
                                    return value;
                                }

                                const matchedBusiness = businessData.find(
                                    (option) =>
                                        String(option.businessName).toLowerCase() ===
                                        value.toLowerCase()
                                );

                                return matchedBusiness ? String(matchedBusiness.id) : null;
                            })
                            .filter(Boolean);

                        nextSelectedOptions[client.id] = businessIds;
                    } else {
                        nextSelectedOptions[client.id] = [];
                    }

                    hasChanges = true;
                });
            });

            return hasChanges ? nextSelectedOptions : prevSelectedOptions;
        });
    }, [groupedClients, businessData]);

    const handleCheckboxChange = (clientId, checkboxValue) => {
        setClientSelectedOptions((prevSelectedOptions) => {
            const selectedOptions = prevSelectedOptions[clientId] || [];
            if (selectedOptions.includes(checkboxValue)) {
                return {
                    ...prevSelectedOptions,
                    [clientId]: selectedOptions.filter((item) => item !== checkboxValue),
                };
            } else {
                return {
                    ...prevSelectedOptions,
                    [clientId]: [...selectedOptions, checkboxValue],
                };
            }
        });
    };

    const handleSelectAllChange = (clientId, event) => {
        setClientSelectedOptions((prevSelectedOptions) => {
            if (event.target.checked) {
                const allBusinessIds = businessData.map((option) => String(option.id));
                return { ...prevSelectedOptions, [clientId]: allBusinessIds };
            } else {
                return { ...prevSelectedOptions, [clientId]: [] };
            }
        });
    };

    const showModalValidity = (client) => {
        setIsModalOpenValidity(true);
        setSelectedClient(client)
    }

    const hideModalValidity = () => {
        setIsModalOpenValidity(false);
        setSelectedClient(null);
    };

    const handleValiditySaveSuccess = async () => {
        hideModalValidity();
        const clientData = await fetchClientData(pageNo, searchCriteria, searchText);

        const client = clientData.find(item => item.id == selectedClient.id);

        const companyInfo = getRegisterClientForBranch(client);
        const accountManagerInfo = accountManagerInformationData.find(
            (info) => info.branchId === client.id && info.active
        );
        const accountManagerName = accountManagerInfo
            ? accountManagerMasterData.find((manager) => manager.id === accountManagerInfo.acManagerId)?.name
            : '';
        const packageName = packageData.find((pkg) => pkg.id === client.packageId)?.name || '';
        const urlUniqueKeyValue = client.urlUniqueKey || client.UrlUniqueKey || client.urluniquekey || client.URLUniqueKey || '';

        const payload = {
            branchId: client.id,
            clientId: client.clientId,
            registeredClientId: client.clientId,
            firmName: companyInfo?.FirmName || '',
            branchName: client.branchName || '',
            emailId: client.emailId || '',
            installationDate: client.InstallationDate || '',
            expiryDate: client.ExpiryDate || '',
            stopWorking: client.StopWorking === true,
            businessType: client.businessType || '',
            packageId: client.packageId || '',
            packageName: packageName,
            accountManagerId: accountManagerInfo?.acManagerId || '',
            accountManagerName: accountManagerName || '',
            universalLink: client.universalLink || client.UniversalLink || '',
            liveUrl: client.liveUrl || client.liveURL || client.LiveUrl || client.LiveURL || '',
            urlUniqueKey: urlUniqueKeyValue,
            fingerPrint: client.fingerPrint,
            lastLogin: client.lastLogin,
            DBToken: client.DBToken,
            LoginCounter: client.LoginCounter,
            serviceSupportExpiry: client.serviceSupportExpiry,
            hashValue: client.hashValue,
        };

        const token = await generateEncryptedToken(payload);
        const response = await Branches.UpdateBranch(client.id, { token, Token: token });
        setBranchData(prev =>
            prev.map(item =>
                item.id === client.id
                    ? { ...item, token: token }
                    : item
            )
        );
    };

    const convertToGMT530 = (isoString) => {
        const d = new Date(isoString);
        const day = String(d.getUTCDate()).padStart(2, '0');
        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
        const year = d.getUTCFullYear();

        return `${day}-${month}-${year}`;
    };

    const toDateInputValue = (isoString) => {
        if (!isoString) return '';
        const utcDate = new Date(isoString);
        if (Number.isNaN(utcDate.getTime())) return '';
        const gmt530Offset = utcDate.getTime() + (5 * 60 * 60 * 1000) + (30 * 60 * 1000);
        return new Date(gmt530Offset).toISOString().split('T')[0];
    };

    const toggleFirmCollapse = (firmName) => {
        setCollapsedFirms((prev) => ({
            ...prev,
            [firmName]: !prev[firmName],
        }));
    };

    const expandAllFirms = () => {
        setCollapsedFirms({});
    };

    const collapseAllFirms = () => {
        const next = {};
        branchData.forEach((client) => {
            const registerClientdata = getRegisterClientForBranch(client);
            const firmName = (registerClientdata?.FirmName || '').trim() || 'Firm Name Not Found';
            next[firmName] = true;
        });
        setCollapsedFirms(next);
    };

    const getFirmAccountManagerId = (firmBranches) => {
        if (!firmBranches?.length) return '';
        const managerIds = firmBranches.map((branch) => {
            const info = accountManagerInformationData.find(
                (item) => item.branchId === branch.id && item.active
            );
            return info?.acManagerId != null ? String(info.acManagerId) : '';
        });
        const firstId = managerIds[0] || '';
        const allSame = managerIds.every((id) => id === firstId);
        return allSame ? firstId : '';
    };

    const handleSaveFirmAccountManager = async (e, firmName, firmBranches) => {
        e.preventDefault();
        e.stopPropagation();

        const firmKey = firmName;
        const selectedManagerId =
            firmAccountManagerEdits[firmKey] !== undefined
                ? firmAccountManagerEdits[firmKey]
                : getFirmAccountManagerId(firmBranches);

        if (!selectedManagerId) {
            Swal.fire({
                title: "Info",
                text: "Please select an account manager.",
                icon: "info",
                width: "320px"
            });
            return;
        }

        if (!firmBranches?.length) {
            Swal.fire({
                title: "Info",
                text: "No branches found for this company.",
                icon: "info",
                width: "320px"
            });
            return;
        }

        const confirmResult = await Swal.fire({
            title: "Are you sure?",
            text: `This will update Account Manager for all ${firmBranches.length} branch(es) under ${firmName}.`,
            showCancelButton: true,
            confirmButtonText: "Yes, Update",
            cancelButtonText: "Cancel",
            width: "340px"
        });

        if (!confirmResult.isConfirmed) return;

        setSavingFirmAccountManager((prev) => ({ ...prev, [firmKey]: true }));
        try {
            let successCount = 0;
            const failedIds = [];

            for (const branch of firmBranches) {
                try {
                    const sdata = {
                        branchId: branch.id,
                        accountManagerId: selectedManagerId,
                        active: true,
                    };
                    const exist = await AccountManagerInformationApi.GetAccountManagerInformationById(branch.id);
                    let response;
                    if (exist?.StatusCode === 200) {
                        response = await AccountManagerInformationApi.UpdateAccountManagerInformation(branch.id, sdata);
                    } else {
                        response = await AccountManagerInformationApi.CreateAccountManagerInformation(sdata);
                    }
                    const statusCode = Number(response?.StatusCode ?? response?.statusCode ?? response?.status);
                    const isSuccess =
                        (statusCode >= 200 && statusCode < 300) ||
                        response?.success === true ||
                        response?.Success === true ||
                        response?.IsSuccess === true;

                    if (isSuccess) {
                        successCount += 1;
                    } else {
                        failedIds.push(branch.id);
                    }
                } catch (err) {
                    console.error(`Firm account manager update failed for branch ${branch.id}:`, err);
                    failedIds.push(branch.id);
                }
            }

            if (successCount > 0) {
                setAccountManagerInformationData((prevData) => {
                    const next = [...prevData];
                    firmBranches.forEach((branch) => {
                        if (failedIds.includes(branch.id)) return;
                        const index = next.findIndex((info) => info.branchId === branch.id && info.active);
                        if (index !== -1) {
                            next[index] = {
                                ...next[index],
                                acManagerId: Number(selectedManagerId) || selectedManagerId,
                            };
                        } else {
                            next.push({
                                id: null,
                                branchId: branch.id,
                                acManagerId: Number(selectedManagerId) || selectedManagerId,
                                active: true,
                                creationDate: new Date().toISOString(),
                            });
                        }
                    });
                    return next;
                });
                await fetchAccountManagerInformationData();
                const successBranches = firmBranches.filter((branch) => !failedIds.includes(branch.id));
                await regenerateTokensForFirmBranches(successBranches, {
                    accountManagerId: selectedManagerId,
                });
            }

            setFirmAccountManagerEdits((prev) => {
                const next = { ...prev };
                delete next[firmKey];
                return next;
            });

            if (failedIds.length === 0) {
                Swal.fire({
                    title: "Updated",
                    text: `Account Manager updated for all ${successCount} branch(es) of ${firmName}.`,
                    icon: "success",
                    width: "320px"
                });
            } else {
                Swal.fire({
                    title: successCount > 0 ? "Partial Update" : "Failed",
                    text: `Updated ${successCount} branch(es). Failed: ${failedIds.length}.`,
                    icon: successCount > 0 ? "warning" : "error",
                    width: "320px"
                });
            }
        } catch (error) {
            console.error("Firm account manager update failed:", error);
            Swal.fire({
                title: "Error",
                text: "Failed to update account manager for this company.",
                icon: "error",
                width: "320px"
            });
        } finally {
            setSavingFirmAccountManager((prev) => ({ ...prev, [firmKey]: false }));
        }
    };

    const getFirmCommonDateValue = (firmBranches, fieldKeys) => {
        if (!firmBranches?.length) return '';
        const values = firmBranches.map((branch) => {
            for (const key of fieldKeys) {
                if (branch[key]) return toDateInputValue(branch[key]);
            }
            return '';
        });
        const firstValue = values[0] || '';
        return values.every((value) => value === firstValue) ? firstValue : '';
    };

    const getFirmCommonStatusValue = (firmBranches) => {
        if (!firmBranches?.length) return '';
        const values = firmBranches.map((branch) => (branch.StopWorking === true ? 'inactive' : 'active'));
        const firstValue = values[0];
        return values.every((value) => value === firstValue) ? firstValue : '';
    };

    const isApiResponseSuccess = (response) => {
        const statusCode = Number(response?.StatusCode ?? response?.statusCode ?? response?.status);
        return (
            (statusCode >= 200 && statusCode < 300) ||
            response?.success === true ||
            response?.Success === true ||
            response?.IsSuccess === true
        );
    };

    const regenerateTokensForFirmBranches = async (firmBranches, overrides = {}) => {
        if (!firmBranches?.length) return;

        const tokenUpdates = {};
        for (const client of firmBranches) {
            try {
                const companyInfo = getRegisterClientForBranch(client);
                let accountManagerId = overrides.accountManagerId;
                let accountManagerName = overrides.accountManagerName;

                if (accountManagerId === undefined) {
                    const accountManagerInfo = accountManagerInformationData.find(
                        (info) => info.branchId === client.id && info.active
                    );
                    accountManagerId = accountManagerInfo?.acManagerId || '';
                    accountManagerName = accountManagerInfo
                        ? accountManagerMasterData.find((manager) => manager.id === accountManagerInfo.acManagerId)?.name || ''
                        : '';
                } else {
                    accountManagerName =
                        accountManagerMasterData.find(
                            (manager) => String(manager.id) === String(accountManagerId)
                        )?.name || '';
                }

                const packageName = packageData.find((pkg) => pkg.id === client.packageId)?.name || '';
                const urlUniqueKeyValue =
                    client.urlUniqueKey || client.UrlUniqueKey || client.urluniquekey || client.URLUniqueKey || '';

                const payload = {
                    branchId: client.id,
                    clientId: client.clientId,
                    registeredClientId: client.clientId,
                    firmName: companyInfo?.FirmName || '',
                    branchName: client.branchName || '',
                    emailId: client.emailId || '',
                    installationDate:
                        overrides.installationDate !== undefined
                            ? overrides.installationDate
                            : (client.InstallationDate || ''),
                    expiryDate:
                        overrides.expiryDate !== undefined
                            ? overrides.expiryDate
                            : (client.ExpiryDate || ''),
                    stopWorking:
                        overrides.stopWorking !== undefined
                            ? overrides.stopWorking
                            : (client.StopWorking === true),
                    businessType: client.businessType || '',
                    packageId: client.packageId || '',
                    packageName: packageName,
                    accountManagerId: accountManagerId || '',
                    accountManagerName: accountManagerName || '',
                    universalLink: client.universalLink || client.UniversalLink || '',
                    liveUrl: client.liveUrl || client.liveURL || client.LiveUrl || client.LiveURL || '',
                    urlUniqueKey: urlUniqueKeyValue,
                    fingerPrint: client.fingerPrint,
                    lastLogin: client.lastLogin,
                    DBToken: client.DBToken,
                    LoginCounter: client.LoginCounter,
                    serviceSupportExpiry:
                        overrides.serviceSupportExpiry !== undefined
                            ? overrides.serviceSupportExpiry
                            : (client.serviceSupportExpiry || client.ServiceExpiryDate),
                    hashValue: client.hashValue,
                };

                const token = await generateEncryptedToken(payload);
                const response = await Branches.UpdateBranch(client.id, { token, Token: token });
                if (isApiResponseSuccess(response)) {
                    tokenUpdates[client.id] = token;
                }
            } catch (err) {
                console.error(`Token regenerate failed for branch ${client.id}:`, err);
            }
        }

        if (Object.keys(tokenUpdates).length > 0) {
            setBranchData((prevData) =>
                prevData.map((item) =>
                    tokenUpdates[item.id]
                        ? { ...item, token: tokenUpdates[item.id], Token: tokenUpdates[item.id] }
                        : item
                )
            );
        }
    };

    const handleSaveFirmInstallation = async (e, firmName, firmBranches) => {
        e.preventDefault();
        e.stopPropagation();

        const firmKey = firmName;
        const newDate =
            firmInstallationEdits[firmKey] !== undefined
                ? firmInstallationEdits[firmKey]
                : getFirmCommonDateValue(firmBranches, ['InstallationDate', 'installationDate']);

        if (!newDate) {
            Swal.fire({
                title: "Info",
                text: "Please select an installation date.",
                icon: "info",
                width: "320px"
            });
            return;
        }

        if (!firmBranches?.length) return;

        const confirmResult = await Swal.fire({
            title: "Are you sure?",
            text: `This will update Installation Date for all ${firmBranches.length} branch(es) under ${firmName}.`,
            showCancelButton: true,
            confirmButtonText: "Yes, Update",
            cancelButtonText: "Cancel",
            width: "340px"
        });
        if (!confirmResult.isConfirmed) return;

        setSavingFirmInstallation((prev) => ({ ...prev, [firmKey]: true }));
        try {
            const ids = firmBranches.map((branch) => branch.id);
            const response = await Branches.BulkUpdateBranches(ids.join(','), {
                InstallationDate: newDate,
                installationDate: newDate,
            });

            if (isApiResponseSuccess(response)) {
                setBranchData((prevData) =>
                    prevData.map((item) =>
                        ids.includes(item.id)
                            ? { ...item, InstallationDate: newDate, installationDate: newDate }
                            : item
                    )
                );
                setFirmInstallationEdits((prev) => {
                    const next = { ...prev };
                    delete next[firmKey];
                    return next;
                });
                await regenerateTokensForFirmBranches(firmBranches, {
                    installationDate: newDate,
                });
                Swal.fire({
                    title: "Updated",
                    text: `Installation date updated for all ${ids.length} branch(es) of ${firmName}.`,
                    icon: "success",
                    width: "320px"
                });
            } else {
                Swal.fire({
                    title: "Failed",
                    text: response?.error || response?.ErrorMessages || "Failed to update installation date.",
                    icon: "error",
                    width: "320px"
                });
            }
        } catch (error) {
            console.error("Firm installation update failed:", error);
            Swal.fire({
                title: "Error",
                text: "Failed to update installation date for this company.",
                icon: "error",
                width: "320px"
            });
        } finally {
            setSavingFirmInstallation((prev) => ({ ...prev, [firmKey]: false }));
        }
    };

    const handleSaveFirmExpiry = async (e, firmName, firmBranches) => {
        e.preventDefault();
        e.stopPropagation();

        const firmKey = firmName;
        const newDate =
            firmExpiryEdits[firmKey] !== undefined
                ? firmExpiryEdits[firmKey]
                : getFirmCommonDateValue(firmBranches, ['ExpiryDate', 'expiryDate']);

        if (!newDate) {
            Swal.fire({
                title: "Info",
                text: "Please select an expiry date.",
                icon: "info",
                width: "320px"
            });
            return;
        }

        if (!firmBranches?.length) return;

        const confirmResult = await Swal.fire({
            title: "Are you sure?",
            text: `This will update Expiry Date and Service Expiry Date for all ${firmBranches.length} branch(es) under ${firmName}.`,
            showCancelButton: true,
            confirmButtonText: "Yes, Update",
            cancelButtonText: "Cancel",
            width: "340px"
        });
        if (!confirmResult.isConfirmed) return;

        setSavingFirmExpiry((prev) => ({ ...prev, [firmKey]: true }));
        try {
            const ids = firmBranches.map((branch) => branch.id);
            const response = await Branches.BulkUpdateBranches(ids.join(','), {
                ExpiryDate: newDate,
                expiryDate: newDate,
                ServiceExpiryDate: newDate,
                serviceSupportExpiry: newDate,
            });

            if (isApiResponseSuccess(response)) {
                setBranchData((prevData) =>
                    prevData.map((item) =>
                        ids.includes(item.id)
                            ? {
                                ...item,
                                ExpiryDate: newDate,
                                expiryDate: newDate,
                                ServiceExpiryDate: newDate,
                                serviceSupportExpiry: newDate,
                            }
                            : item
                    )
                );
                setFirmExpiryEdits((prev) => {
                    const next = { ...prev };
                    delete next[firmKey];
                    return next;
                });
                await regenerateTokensForFirmBranches(firmBranches, {
                    expiryDate: newDate,
                    serviceSupportExpiry: newDate,
                });
                Swal.fire({
                    title: "Updated",
                    text: `Expiry and Service Expiry dates updated for all ${ids.length} branch(es) of ${firmName}.`,
                    icon: "success",
                    width: "320px"
                });
            } else {
                Swal.fire({
                    title: "Failed",
                    text: response?.error || response?.ErrorMessages || "Failed to update expiry date.",
                    icon: "error",
                    width: "320px"
                });
            }
        } catch (error) {
            console.error("Firm expiry update failed:", error);
            Swal.fire({
                title: "Error",
                text: "Failed to update expiry date for this company.",
                icon: "error",
                width: "320px"
            });
        } finally {
            setSavingFirmExpiry((prev) => ({ ...prev, [firmKey]: false }));
        }
    };

    const handleSaveFirmStatus = async (e, firmName, firmBranches, targetStatusValue) => {
        e.preventDefault();
        e.stopPropagation();

        const firmKey = firmName;
        const currentStatus = getFirmCommonStatusValue(firmBranches);
        const statusValue =
            targetStatusValue ||
            (currentStatus === 'inactive' ? 'active' : 'inactive');

        if (!firmBranches?.length) return;

        const stopWorking = statusValue === 'inactive';
        const confirmResult = await Swal.fire({
            title: "Are you sure?",
            text: `This will set Status to ${statusValue === 'active' ? 'Active' : 'Inactive'} for all ${firmBranches.length} branch(es) under ${firmName}.`,
            showCancelButton: true,
            confirmButtonText: "Yes, Update",
            cancelButtonText: "Cancel",
            width: "340px"
        });
        if (!confirmResult.isConfirmed) return;

        setSavingFirmStatus((prev) => ({ ...prev, [firmKey]: true }));
        try {
            const ids = firmBranches.map((branch) => branch.id);
            const response = await Branches.BulkUpdateBranches(ids.join(','), {
                StopWorking: stopWorking,
                stopWorking,
            });

            if (isApiResponseSuccess(response)) {
                setBranchData((prevData) =>
                    prevData.map((item) =>
                        ids.includes(item.id)
                            ? { ...item, StopWorking: stopWorking, stopWorking }
                            : item
                    )
                );
                await regenerateTokensForFirmBranches(firmBranches, {
                    stopWorking,
                });
                Swal.fire({
                    title: "Updated",
                    text: `Status updated for all ${ids.length} branch(es) of ${firmName}.`,
                    icon: "success",
                    width: "320px"
                });
            } else {
                Swal.fire({
                    title: "Failed",
                    text: response?.error || response?.ErrorMessages || "Failed to update status.",
                    icon: "error",
                    width: "320px"
                });
            }
        } catch (error) {
            console.error("Firm status update failed:", error);
            Swal.fire({
                title: "Error",
                text: "Failed to update status for this company.",
                icon: "error",
                width: "320px"
            });
        } finally {
            setSavingFirmStatus((prev) => ({ ...prev, [firmKey]: false }));
        }
    };

    const handleInstallationDateSave = async (e, client) => {
        e.preventDefault();
        e.stopPropagation();
        const clientId = client.id;
        const newDate =
            installationDateEdits[clientId] !== undefined
                ? installationDateEdits[clientId]
                : toDateInputValue(client.InstallationDate);

        if (!newDate) {
            Swal.fire({
                title: "Info",
                text: "Please select an installation date.",
                icon: "info",
                width: "320px"
            });
            return;
        }

        setSavingInstallationIds((prev) => ({ ...prev, [clientId]: true }));
        try {
            const response = await Branches.UpdateBranch(clientId, {
                InstallationDate: newDate,
                installationDate: newDate,
            });
            const statusCode = Number(response?.StatusCode ?? response?.statusCode ?? response?.status);
            const isSuccess =
                (statusCode >= 200 && statusCode < 300) ||
                response?.success === true ||
                response?.Success === true ||
                response?.IsSuccess === true;

            if (isSuccess) {
                setBranchData((prevData) =>
                    prevData.map((item) =>
                        item.id === clientId
                            ? { ...item, InstallationDate: newDate, installationDate: newDate }
                            : item
                    )
                );
                setInstallationDateEdits((prev) => {
                    const next = { ...prev };
                    delete next[clientId];
                    return next;
                });
                Swal.fire({
                    title: "Updated",
                    text: `Installation date updated for ${client.branchName || `ID ${clientId}`}.`,
                    icon: "success",
                    width: "320px"
                });
            } else {
                Swal.fire({
                    title: "Failed",
                    text: response?.error || response?.ErrorMessages || "Failed to update installation date.",
                    icon: "error",
                    width: "320px"
                });
            }
        } catch (error) {
            console.error("Installation date update failed:", error);
            Swal.fire({
                title: "Error",
                text: "Failed to update installation date.",
                icon: "error",
                width: "320px"
            });
        } finally {
            setSavingInstallationIds((prev) => ({ ...prev, [clientId]: false }));
        }
    };

    const getBranchFieldValue = (client, keys) => {
        for (const key of keys) {
            if (client[key] !== undefined && client[key] !== null && client[key] !== '') {
                return client[key];
            }
        }
        return '-';
    };

    const toBase64 = (bytes) => {
        let binary = '';
        const chunkSize = 0x8000;
        for (let i = 0; i < bytes.length; i += chunkSize) {
            const chunk = bytes.subarray(i, i + chunkSize);
            binary += String.fromCharCode(...chunk);
        }
        return btoa(binary);
    };

    const toBase64Url = (value) => {
        const base64 = typeof value === 'string'
            ? toBase64(new TextEncoder().encode(value))
            : toBase64(value);
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    };

    const generateEncryptedToken = async (payload) => {
        // JWT-compatible shape so decoding returns branch/company fields directly.
        const header = { alg: 'HS256', typ: 'JWT' };

        const headerPart = toBase64Url(JSON.stringify(header));
        const payloadPart = toBase64Url(JSON.stringify(payload));
        const signaturePart = toBase64Url(window.crypto.getRandomValues(new Uint8Array(32)));

        return `${headerPart}.${payloadPart}.${signaturePart}`;
    };

    const fetchPackageData = async () => {
        setLoading(true);
        const response = await Package.GetAllPackage()
        if (response) {
            console.log("response", response);
            setPackageData(response.Result);
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchPackageData()
    }, []);

    const fetchAccountManagerInformationData = async () => {
        setLoading(true);
        const response = await AccountManagerInformationApi.GetAllAccountManagerInformation()
        if (response) {
            console.log("response", response);
            setAccountManagerInformationData(response.Result)
        }
        setLoading(false);
    }
    useEffect(() => {
        fetchAccountManagerInformationData();
    }, []);

    const fetchAccountManagerMasterdata = async () => {
        setLoading(true);
        const response = await AccountManager.GetAccountByLoginType('accountmanager')
        if (response) {
            setAccountManagerMasterData(response.Result);
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchAccountManagerMasterdata();
    }, []);

    useEffect(() => {
        return () => {
            if (copyTooltipTimerRef.current) {
                clearTimeout(copyTooltipTimerRef.current);
            }
        };
    }, []);

    // Toggle "Generate Token For All" button visibility with Alt + M
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.altKey && (e.key === 'm' || e.key === 'M')) {
                e.preventDefault();
                setShowGenerateTokenForAll((prev) => !prev);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleServiceExpiryDateSave = async (id, data, client) => {
        const sdata = { StopWorking: data }
        const updatedPayload = {
            stopWorking: data,
            branchId: client.id,
            clientId: client.clientId,
            registeredClientId: client.clientId,
            firmName: client.branchName || '',
            branchName: client.branchName || '',
            emailId: client.emailId || '',
            installationDate: client.InstallationDate || '',
            expiryDate: client.ExpiryDate || '',
            businessType: client.businessType || '',
            packageId: client.packageId || '',
            packageName: client.packageName,
            accountManagerId: client.accountManagerInfos[0]?.accountManager?.id || '',
            accountManagerName: client.accountManagerInfos[0]?.accountManager?.name || '',
            universalLink: client.universalLink || client.UniversalLink || '',
            liveUrl: client.liveUrl || client.liveURL || client.LiveUrl || client.LiveURL || '',
            urlUniqueKey: client.urlUniqueKey,
            fingerPrint: client.fingerPrint,
            lastLogin: client.lastLogin,
            //DBToken: client.DBToken,
            LoginCounter: client.LoginCounter,
            serviceSupportExpiry: client.ServiceExpiryDate,
            hashValue: client.hashValue,
        };
        try {
            const response = await Branches.UpdateBranch(id, sdata)

            const token = await generateEncryptedToken(updatedPayload);
            const branchResponse = await Branches.UpdateBranch(id, { token, Token: token });
            setBranchData(prev =>
                prev.map(item =>
                    item.id === id
                        ? { ...item, token: token }
                        : item
                )
            );
            if (response.StatusCode === 200) {
                console.log("response", response);
                fetchClientData()
            }
        } catch (error) {
            console.error(error);
        }
    }

    const handleAccountManagerChange = async (id, newManagerId, client) => {

        setAccountManagerInformationData(prevData => {
            const index = prevData.findIndex(info => info.branchId === id && info.active);

            if (index !== -1) {
                const updated = [...prevData];
                updated[index] = { ...updated[index], acManagerId: newManagerId };
                return updated;
            } else {
                return [
                    ...prevData,
                    {
                        id: null,
                        branchId: id,
                        acManagerId: newManagerId,
                        active: true,
                        creationDate: new Date().toISOString()
                    }
                ];
            }
        });

        console.log("accountManagerInformationData", accountManagerInformationData);

        const sdata = {
            branchId: id,
            accountManagerId: newManagerId,
            active: true,
        }
        console.log("sdata", sdata);
        try {
            const exist = await AccountManagerInformationApi.GetAccountManagerInformationById(id)
            if (exist?.StatusCode === 200) {
                const response = await AccountManagerInformationApi.UpdateAccountManagerInformation(id, sdata)
                if (response.StatusCode === 200) {
                    console.log("response", response);
                    fetchClientData()
                    fetchRegisteredClients()
                }
            } else {
                const response = await AccountManagerInformationApi.CreateAccountManagerInformation(sdata)
                if (response.StatusCode === 201) {
                    console.log("response", response);
                    fetchClientData()
                    fetchRegisteredClients()
                }
            }
        } catch (error) {
            console.error(error);
        }
        /************* Generate Token*************** */
        const accountManagerInfo = accountManagerInformationData.find(
            (info) => info.branchId === client.id && info.active
        );
        const accountManagerName = accountManagerInfo
            ? accountManagerMasterData.find((manager) => manager.id === newManagerId)?.name
            : '';
        const updatedPayload = {
            stopWorking: client.StopWorking,
            branchId: client.id,
            clientId: client.clientId,
            registeredClientId: client.clientId,
            firmName: client.branchName || '',
            branchName: client.branchName || '',
            emailId: client.emailId || '',
            installationDate: client.InstallationDate || '',
            expiryDate: client.ExpiryDate || '',
            businessType: client.businessType || '',
            packageId: client.packageId || '',
            packageName: client.packageName,
            accountManagerId: newManagerId || '',
            accountManagerName: accountManagerName || '',
            universalLink: client.universalLink || client.UniversalLink || '',
            liveUrl: client.liveUrl || client.liveURL || client.LiveUrl || client.LiveURL || '',
            urlUniqueKey: client.urlUniqueKey,
            fingerPrint: client.fingerPrint,
            lastLogin: client.lastLogin,
            //DBToken: client.DBToken,
            LoginCounter: client.LoginCounter,
            serviceSupportExpiry: client.ServiceExpiryDate,
            hashValue: client.hashValue,
        };
        const token = await generateEncryptedToken(updatedPayload);
        const branchResponse = await Branches.UpdateBranch(id, { token, Token: token });
        setBranchData(prev =>
            prev.map(item =>
                item.id === id
                    ? { ...item, token: token }
                    : item
            )
        );

    }

    const handlePackageChange1 = async (clientId, newPackageId, client) => {
        setBranchData((prevData) => {
            return prevData.map(client => {
                if (client.id === clientId) {
                    return { ...client, packageId: newPackageId };
                }
                return client;
            });
        });

        const sdata = {
            packageId: newPackageId,
        };

        try {
            const response = await Branches.UpdateBranch(clientId, sdata);
            if (response.StatusCode === 200) {
                console.log("Package updated successfully:", response);
            }
        } catch (error) {
            console.error("Error updating package:", error);
        }
        const packageName = packageData.find((pkg) => pkg.id === newPackageId)?.name || '';
        const updatedPayload = {
            stopWorking: client.StopWorking,
            branchId: clientId,
            clientId: client.clientId,
            registeredClientId: client.clientId,
            firmName: client.branchName || '',
            branchName: client.branchName || '',
            emailId: client.emailId || '',
            installationDate: client.InstallationDate || '',
            expiryDate: client.ExpiryDate || '',
            businessType: client.businessType || '',
            packageId: newPackageId || '',
            packageName: packageName,
            accountManagerId: client.accountManagerInfos[0]?.accountManager?.id || '',
            accountManagerName: client.accountManagerInfos[0]?.accountManager?.name || '',
            universalLink: client.universalLink || client.UniversalLink || '',
            liveUrl: client.liveUrl || client.liveURL || client.LiveUrl || client.LiveURL || '',
            urlUniqueKey: client.urlUniqueKey,
            fingerPrint: client.fingerPrint,
            lastLogin: client.lastLogin,
            //DBToken: client.DBToken,
            LoginCounter: client.LoginCounter,
            serviceSupportExpiry: client.ServiceExpiryDate,
            hashValue: client.hashValue,
        };
        const token = await generateEncryptedToken(updatedPayload);
        const branchResponse = await Branches.UpdateBranch(clientId, { token, Token: token });
        setBranchData(prev =>
            prev.map(item =>
                item.id === clientId
                    ? { ...item, token: token }
                    : item
            )
        );
    };

    const handleBusinessTypeSave = async (e, clientId, client) => {
        e.preventDefault()
        const selectedOptions = clientSelectedOptions[clientId] || [];
        console.log(`Saving ${clientSelectedOptions} for client ${clientId}`);
        const businessType = selectedOptions.join(',');
        const data = {
            businessType: businessType
        }
        try {
            const response = await Branches.UpdateBranch(clientId, data)
            if (response) {
                setOpenDropdowns({})
                console.log("response", response);
                const branchName = branchData.find((item) => item.id === clientId)?.branchName || `ID ${clientId}`;
                Swal.fire({
                    title: "Updated",
                    text: `Business Type updated for ${branchName}.`,
                    icon: "success",
                    width: "320px"
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                title: "Error",
                text: "Failed to update Business Type.",
                icon: "error",
                width: "320px"
            });
        }
        const updatedPayload = {
            stopWorking: client.StopWorking,
            branchId: client.id,
            clientId: client.clientId,
            registeredClientId: client.clientId,
            firmName: client.branchName || '',
            branchName: client.branchName || '',
            emailId: client.emailId || '',
            installationDate: client.InstallationDate || '',
            expiryDate: client.ExpiryDate || '',
            businessType: businessType || '',
            packageId: client.packageId || '',
            packageName: client.packageName,
            accountManagerId: client.accountManagerInfos[0]?.accountManager?.id || '',
            accountManagerName: client.accountManagerInfos[0]?.accountManager?.name || '',
            universalLink: client.universalLink || client.UniversalLink || '',
            liveUrl: client.liveUrl || client.liveURL || client.LiveUrl || client.LiveURL || '',
            urlUniqueKey: client.urlUniqueKey,
            fingerPrint: client.fingerPrint,
            lastLogin: client.lastLogin,
            //DBToken: client.DBToken,
            LoginCounter: client.LoginCounter,
            serviceSupportExpiry: client.ServiceExpiryDate,
            hashValue: client.hashValue,
        };
        const token = await generateEncryptedToken(updatedPayload);
        const branchResponse = await Branches.UpdateBranch(clientId, { token, Token: token });
        setBranchData(prev =>
            prev.map(item =>
                item.id === clientId
                    ? { ...item, token: token }
                    : item
            )
        );
    };

    const handleDeleteClient = async (e, clientId) => {
        try {
            e.preventDefault();
            const result = await Swal.fire({
                title: "Are you sure?",
                text: "Are you sure you want to delete this client?",
                //icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes delete it !!",
                width: "300px",
                customClass: {
                    title: "swal2-custom-title",
                    confirmButton: "my-confirm-button",
                    cancelButton: "my-cancel-button",

                    popup: "swal2-custom-popup",
                    htmlContainer: "swal2-custom-text",
                    icon: "swal2-custom-icon",
                },
            })
            if (result.isConfirmed) {
                setLoading(true);
                const stored = localStorage.getItem('authUser');
                const parsed = JSON.parse(stored);

                const otpResponse = await Auth.generateOtpV1(parsed?.reservedField1 || parsed?.email);
                setLoading(false);

                const { value: otp } = await Swal.fire({
                    title: "Enter OTP",
                    html: `
                        <div style="display:flex;justify-content:center;gap:8px;">
                            <input id="otp1" maxlength="1" class="otp-box" inputmode="numeric" />
                            <input id="otp2" maxlength="1" class="otp-box" inputmode="numeric" />
                            <input id="otp3" maxlength="1" class="otp-box" inputmode="numeric" />
                            <input id="otp4" maxlength="1" class="otp-box" inputmode="numeric" />
                            <input id="otp5" maxlength="1" class="otp-box" inputmode="numeric" />
                            <input id="otp6" maxlength="1" class="otp-box" inputmode="numeric" />
                        </div>
                    `,
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: "Verify",

                    didOpen: () => {
                        const inputs = [...document.querySelectorAll(".otp-box")];
                        const confirmBtn = Swal.getConfirmButton();

                        inputs[0].focus();

                        inputs.forEach((input, index) => {
                            input.addEventListener("input", (e) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, "");

                                if (e.target.value && index < inputs.length - 1) {
                                    inputs[index + 1].focus();
                                }
                            });

                            input.addEventListener("keydown", (e) => {
                                // backspace behavior
                                if (e.key === "Backspace" && !input.value && index > 0) {
                                    inputs[index - 1].focus();
                                }

                                // ENTER on last input triggers verify
                                if (e.key === "Enter") {
                                    e.preventDefault();

                                    const otp = inputs.map(i => i.value).join("");

                                    if (otp.length === 6) {
                                        confirmBtn.click();
                                    }
                                }
                            });
                        });
                    },

                    preConfirm: async () => {
                        const otp = [
                            "otp1", "otp2", "otp3", "otp4", "otp5", "otp6"
                        ].map(id => document.getElementById(id).value).join("");

                        if (otp.length !== 6) {
                            Swal.showValidationMessage("Enter 6-digit OTP");
                            return false;
                        }

                        setLoading(true);
                        const verifyOtp = await Auth.verifyOtpV1((parsed?.reservedField1 || parsed?.email), otp);
                        setLoading(false);

                        if (!verifyOtp.IsSuccess) {
                            Swal.showValidationMessage("Invalid OTP. Try again.");
                            return false;
                        }

                        return otp;
                    }
                });

                if (!otp) return;
                setLoading(true);
                await Branches.DeleteClients(clientId);

                setBranchData(prev => prev.filter(c => c.id !== clientId));
                setLoading(false);

                Swal.fire({
                    title: "Deleted!!",
                    text: "Client has been deleted successfully.",
                    icon: "success",
                    width: "300px",
                });
            }
        } catch (error) {
            console.error(error);
        }
    }

    const handleGenerateToken = async (e, client) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            const companyInfo = getRegisterClientForBranch(client);
            const accountManagerInfo = accountManagerInformationData.find(
                (info) => info.branchId === client.id && info.active
            );
            const accountManagerName = accountManagerInfo
                ? accountManagerMasterData.find((manager) => manager.id === accountManagerInfo.acManagerId)?.name
                : '';
            const packageName = packageData.find((pkg) => pkg.id === client.packageId)?.name || '';
            const urlUniqueKeyValue = client.urlUniqueKey || client.UrlUniqueKey || client.urluniquekey || client.URLUniqueKey || '';

            const payload = {
                branchId: client.id,
                clientId: client.clientId,
                registeredClientId: client.clientId,
                firmName: companyInfo?.FirmName || '',
                branchName: client.branchName || '',
                emailId: client.emailId || '',
                // mobileNo: client.mobileNo || '',
                // address: client.address || '',
                // city: client.city || '',
                // state: client.state || '',
                // country: client.country || '',
                installationDate: client.InstallationDate || '',
                expiryDate: client.ExpiryDate || '',
                stopWorking: client.StopWorking === true,
                businessType: client.businessType || '',
                packageId: client.packageId || '',
                packageName: packageName,
                accountManagerId: accountManagerInfo?.acManagerId || '',
                accountManagerName: accountManagerName || '',
                universalLink: client.universalLink || client.UniversalLink || '',
                liveUrl: client.liveUrl || client.liveURL || client.LiveUrl || client.LiveURL || '',
                urlUniqueKey: urlUniqueKeyValue,
                // generatedAt: new Date().toISOString(),
                // version: 1
                fingerPrint: client.fingerPrint,
                lastLogin: client.lastLogin,
                DBToken: client.DBToken,
                LoginCounter: client.LoginCounter,
                serviceSupportExpiry: client.serviceSupportExpiry,
                hashValue: client.hashValue,
            };

            const token = await generateEncryptedToken(payload);
            const response = await Branches.UpdateBranch(client.id, { token, Token: token });
            const statusCode = Number(response?.StatusCode ?? response?.statusCode ?? response?.status);
            const isSuccess =
                (statusCode >= 200 && statusCode < 300) ||
                response?.success === true ||
                response?.Success === true ||
                response?.IsSuccess === true;

            if (isSuccess) {
                setBranchData((prevData) =>
                    prevData.map((item) =>
                        item.id === client.id ? { ...item, token, Token: token } : item
                    )
                );
                Swal.fire({
                    title: "Success",
                    text: "Token generated and updated successfully.",
                    icon: "success",
                    width: "320px"
                });
            } else {
                Swal.fire({
                    title: "Failed",
                    text: response?.error || response?.Message || "Token generation failed. Please try again.",
                    icon: "error",
                    width: "320px"
                });
            }
        } catch (error) {
            console.error("Error generating token:", error);
            Swal.fire({
                title: "Error",
                text: error?.response?.data?.error || error?.message || "Unable to generate token.",
                icon: "error",
                width: "320px"
            });
        }
    };

    const handleGenerateTokenForAll = async () => {
        if (!branchData || branchData.length === 0) {
            Swal.fire({
                title: "Info",
                text: "No clients in the current list to generate tokens for.",
                icon: "info",
                width: "320px"
            });
            return;
        }

        const confirmResult = await Swal.fire({
            title: "Are you sure?",
            text: `This will generate and update tokens for all ${branchData.length} client(s) in the current list.`,
            showCancelButton: true,
            confirmButtonText: "Yes, Generate All",
            cancelButtonText: "Cancel",
            width: "360px"
        });

        if (!confirmResult.isConfirmed) return;

        setLoading(true);
        let successCount = 0;
        let failCount = 0;

        try {
            for (const client of branchData) {
                try {
                    const companyInfo = getRegisterClientForBranch(client);
                    const accountManagerInfo = accountManagerInformationData.find(
                        (info) => info.branchId === client.id && info.active
                    );
                    const accountManagerName = accountManagerInfo
                        ? accountManagerMasterData.find((manager) => manager.id === accountManagerInfo.acManagerId)?.name
                        : '';
                    const packageName = packageData.find((pkg) => pkg.id === client.packageId)?.name || '';
                    const urlUniqueKeyValue = client.urlUniqueKey || client.UrlUniqueKey || client.urluniquekey || client.URLUniqueKey || '';

                    const payload = {
                        branchId: client.id,
                        clientId: client.clientId,
                        registeredClientId: client.clientId,
                        firmName: companyInfo?.FirmName || '',
                        branchName: client.branchName || '',
                        emailId: client.emailId || '',
                        installationDate: client.InstallationDate || '',
                        expiryDate: client.ExpiryDate || '',
                        stopWorking: client.StopWorking === true,
                        businessType: client.businessType || '',
                        packageId: client.packageId || '',
                        packageName: packageName,
                        accountManagerId: accountManagerInfo?.acManagerId || '',
                        accountManagerName: accountManagerName || '',
                        universalLink: client.universalLink || client.UniversalLink || '',
                        liveUrl: client.liveUrl || client.liveURL || client.LiveUrl || client.LiveURL || '',
                        urlUniqueKey: urlUniqueKeyValue,
                        fingerPrint: client.fingerPrint,
                        lastLogin: client.lastLogin,
                        DBToken: client.DBToken,
                        LoginCounter: client.LoginCounter,
                        serviceSupportExpiry: client.serviceSupportExpiry,
                        hashValue: client.hashValue,
                    };

                    const token = await generateEncryptedToken(payload);
                    const response = await Branches.UpdateBranch(client.id, { token, Token: token });
                    const statusCode = Number(response?.StatusCode ?? response?.statusCode ?? response?.status);
                    const isSuccess =
                        (statusCode >= 200 && statusCode < 300) ||
                        response?.success === true ||
                        response?.Success === true ||
                        response?.IsSuccess === true;

                    if (isSuccess) {
                        successCount++;
                        setBranchData((prevData) =>
                            prevData.map((item) =>
                                item.id === client.id ? { ...item, token, Token: token } : item
                            )
                        );
                    } else {
                        failCount++;
                    }
                } catch (err) {
                    console.error(`Error generating token for client ${client.id}:`, err);
                    failCount++;
                }
            }

            Swal.fire({
                title: failCount === 0 ? "Success" : "Completed",
                text: `Tokens generated: ${successCount} succeeded, ${failCount} failed.`,
                icon: failCount === 0 ? "success" : "warning",
                width: "340px"
            });
        } catch (error) {
            console.error("Error in handleGenerateTokenForAll:", error);
            Swal.fire({
                title: "Error",
                text: error?.message || "Unable to generate tokens for all clients.",
                icon: "error",
                width: "320px"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCopyToken = async (e, tokenValue, rowId) => {
        e.preventDefault();
        e.stopPropagation();

        if (!tokenValue || tokenValue === '-') return;

        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(tokenValue);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = tokenValue;
                textArea.style.position = "fixed";
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();

                document.execCommand("copy");
                document.body.removeChild(textArea);
            }

            setCopiedTokenRowId(rowId);

            if (copyTooltipTimerRef.current) {
                clearTimeout(copyTooltipTimerRef.current);
            }

            copyTooltipTimerRef.current = setTimeout(() => {
                setCopiedTokenRowId(null);
            }, 1000);

        } catch (error) {
            console.error("Copy token failed:", error);
        }
    };

    // const handleCopyToken = async (e, tokenValue, rowId) => {
    //     e.preventDefault();
    //     e.stopPropagation();

    //     if (!tokenValue || tokenValue === '-') {
    //         return;
    //     }

    //     try {
    //         await navigator.clipboard.writeText(tokenValue);
    //         setCopiedTokenRowId(rowId);
    //         if (copyTooltipTimerRef.current) {
    //             clearTimeout(copyTooltipTimerRef.current);
    //         }
    //         copyTooltipTimerRef.current = setTimeout(() => {
    //             setCopiedTokenRowId(null);
    //         }, 1000);
    //     } catch (error) {
    //         console.error("Copy token failed:", error);
    //     }
    // };

    const handleBranchSelect = (branchId, checked) => {
        setSelectedBranchIds((prev) => {
            if (checked) {
                return prev.includes(branchId) ? prev : [...prev, branchId];
            }
            return prev.filter((id) => id !== branchId);
        });
    };

    const handleSelectAllBranches = (checked) => {
        const currentPageIds = branchData.map((branch) => branch.id);

        if (checked) {
            setSelectedBranchIds((prev) => [
                ...new Set([...prev, ...currentPageIds])
            ]);
        } else {
            setSelectedBranchIds((prev) =>
                prev.filter((id) => !currentPageIds.includes(id))
            );
        }
    };

    const handleSelectFirmBranches = (firmBranches, checked) => {
        const firmBranchIds = firmBranches.map((branch) => branch.id);
        if (checked) {
            setSelectedBranchIds((prev) => [
                ...new Set([...prev, ...firmBranchIds])
            ]);
        } else {
            setSelectedBranchIds((prev) =>
                prev.filter((id) => !firmBranchIds.includes(id))
            );
        }
    };

    const handleBulkUpdateSelectedBranches = async (e) => {
        e.preventDefault();

        if (!selectedBranchIds.length) {
            Swal.fire({
                title: "Info",
                text: "Please select at least one branch.",
                icon: "info",
                width: "320px"
            });
            return;
        }

        const isAccountManagerBulk = bulkUpdateField === 'accountManagerId';
        const textValue = bulkUpdateText.trim();

        if (isAccountManagerBulk) {
            if (!bulkAccountManagerId) {
                Swal.fire({
                    title: "Info",
                    text: "Please select an account manager.",
                    icon: "info",
                    width: "320px"
                });
                return;
            }
        } else if (!textValue) {
            Swal.fire({
                title: "Info",
                text: "Please enter text before updating.",
                icon: "info",
                width: "320px"
            });
            return;
        }

        const fieldLabel =
            bulkUpdateField === 'universalLink'
                ? 'Universal Link'
                : bulkUpdateField === 'liveUrl'
                    ? 'Live URL'
                    : bulkUpdateField === 'accountManagerId'
                        ? 'Account Manager'
                        : 'URL Unique Key';

        const confirmResult = await Swal.fire({
            title: "Are you sure?",
            text: `This will update ${fieldLabel} for ${selectedBranchIds.length} selected branch(es).`,
            showCancelButton: true,
            confirmButtonText: "Yes, Update",
            cancelButtonText: "Cancel",
            width: "340px"
        });

        if (!confirmResult.isConfirmed) return;

        setLoading(true);
        try {
            if (isAccountManagerBulk) {
                let successCount = 0;
                const failedIds = [];

                for (const branchId of selectedBranchIds) {
                    try {
                        const sdata = {
                            branchId,
                            accountManagerId: bulkAccountManagerId,
                            active: true,
                        };
                        const exist = await AccountManagerInformationApi.GetAccountManagerInformationById(branchId);
                        let response;
                        if (exist?.StatusCode === 200) {
                            response = await AccountManagerInformationApi.UpdateAccountManagerInformation(branchId, sdata);
                        } else {
                            response = await AccountManagerInformationApi.CreateAccountManagerInformation(sdata);
                        }
                        const statusCode = Number(response?.StatusCode ?? response?.statusCode ?? response?.status);
                        const isSuccess =
                            (statusCode >= 200 && statusCode < 300) ||
                            response?.success === true ||
                            response?.Success === true ||
                            response?.IsSuccess === true;

                        if (isSuccess) {
                            successCount += 1;
                        } else {
                            failedIds.push(branchId);
                        }
                    } catch (err) {
                        console.error(`Account manager bulk update failed for branch ${branchId}:`, err);
                        failedIds.push(branchId);
                    }
                }

                if (successCount > 0) {
                    setAccountManagerInformationData((prevData) => {
                        const next = [...prevData];
                        selectedBranchIds.forEach((branchId) => {
                            if (failedIds.includes(branchId)) return;
                            const index = next.findIndex((info) => info.branchId === branchId && info.active);
                            if (index !== -1) {
                                next[index] = { ...next[index], acManagerId: Number(bulkAccountManagerId) || bulkAccountManagerId };
                            } else {
                                next.push({
                                    id: null,
                                    branchId,
                                    acManagerId: Number(bulkAccountManagerId) || bulkAccountManagerId,
                                    active: true,
                                    creationDate: new Date().toISOString(),
                                });
                            }
                        });
                        return next;
                    });
                    await fetchAccountManagerInformationData();
                }

                if (failedIds.length === 0) {
                    Swal.fire({
                        title: "Updated",
                        text: `Updated Account Manager for ${successCount} branch(es) successfully.`,
                        icon: "success",
                        width: "320px"
                    });
                    setIsBulkUpdateModalOpen(false);
                    setBulkUpdateText('');
                    setBulkAccountManagerId('');
                    setSelectedBranchIds([]);
                } else {
                    Swal.fire({
                        title: successCount > 0 ? "Partial Update" : "Failed",
                        text: `Updated ${successCount} branch(es). Failed: ${failedIds.length}.`,
                        icon: successCount > 0 ? "warning" : "error",
                        width: "320px"
                    });
                }
            } else {
                const payload = { [bulkUpdateField]: textValue };
                const response = await Branches.BulkUpdateBranches(selectedBranchIds.join(','), payload);
                const statusCode = Number(response?.StatusCode ?? response?.statusCode ?? response?.status);
                const isSuccess =
                    (statusCode >= 200 && statusCode < 300) ||
                    response?.success === true ||
                    response?.Success === true ||
                    response?.IsSuccess === true;

                if (isSuccess) {
                    const successCount = Number(response?.Result?.updatedCount || selectedBranchIds.length);
                    setBranchData((prevData) =>
                        prevData.map((item) =>
                            selectedBranchIds.includes(item.id)
                                ? { ...item, [bulkUpdateField]: textValue }
                                : item
                        )
                    );

                    Swal.fire({
                        title: "Updated",
                        text: `Updated ${successCount} branch(es) successfully.`,
                        icon: "success",
                        width: "320px"
                    });

                    setIsBulkUpdateModalOpen(false);
                    setBulkUpdateText('');
                    setBulkAccountManagerId('');
                    setSelectedBranchIds([]);
                } else {
                    Swal.fire({
                        title: "Failed",
                        text: response?.error || response?.ErrorMessages || "Bulk update failed. Please try again.",
                        icon: "error",
                        width: "320px"
                    });
                }
            }
        } catch (error) {
            console.error("Bulk update failed:", error);
            Swal.fire({
                title: "Error",
                text: "Bulk update failed. Please try again.",
                icon: "error",
                width: "320px"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenBulkUpdateModal = () => {
        if (!selectedBranchIds.length) {
            Swal.fire({
                title: "Info",
                text: "Please select at least one branch.",
                icon: "info",
                width: "320px"
            });
            return;
        }
        setIsBulkUpdateModalOpen(true);
    };

    const allBranchesSelected = branchData.length > 0 && branchData.every((branch) => selectedBranchIds.includes(branch.id));
    let serialNo = (pageNo - 1) * PAGE_LIMIT;

    const [isModalOpenTask, setIsModalOpenTask] = useState(false);
    const showModalTask = () => {
        if (selectedBranchIds.length === 0) {
            Swal.fire({
                title: "Warning",
                text: "Please select at least one branch to open Notification Popup.",
                icon: "warning",
                width: "340px"
            });
            return;
        }
        setIsModalOpenTask(true);
    }

    const hideModalTask = () => {
        setIsModalOpenTask(false);
    };

    const [selectedTemplate, setSelectedTemplate] = useState({ title: "", description: "" })

    const handleRemarkUpdate = async () => {
        if (!selectedTemplate.title || !selectedTemplate.description) {
            Swal.fire({
                title: "Warning",
                text: "Please Enter message or select a template.",
                icon: "warning",
                width: "320px"
            });
            return;
        }

        if (!scheduleData.fromDate) {
            Swal.fire({
                title: "Warning",
                text: "Please select From Date.",
                icon: "warning",
                width: "320px"
            });
            return;
        }

        if (!scheduleData.toDate) {
            Swal.fire({
                title: "Warning",
                text: "Please select To Date.",
                icon: "warning",
                width: "320px"
            });
            return;
        }

        if (!scheduleData.startTime) {
            Swal.fire({
                title: "Warning",
                text: "Please select Start Time.",
                icon: "warning",
                width: "320px"
            });
            return;
        }

        if (!scheduleData.endTime) {
            Swal.fire({
                title: "Warning",
                text: "Please select End Time.",
                icon: "warning",
                width: "320px"
            });
            return;
        }

        if (scheduleData.fromDate > scheduleData.toDate) {
            Swal.fire({
                title: "Warning",
                text: "From Date cannot be after To Date.",
                icon: "warning",
                width: "320px"
            });
            return;
        }

        if (scheduleData.fromDate === scheduleData.toDate && scheduleData.startTime > scheduleData.endTime) {
            Swal.fire({
                title: "Warning",
                text: "Start Time cannot be after End Time.",
                icon: "warning",
                width: "320px"
            });
            return;
        }

        const currentDate = new Date().toISOString().split("T")[0];
        const currentTime = new Date().toTimeString().split(" ")[0];

        const payload = {
            title: selectedTemplate.title,
            textMessage: selectedTemplate.description,
            branch: selectedBranchIds.join(','),
            fromDate: scheduleData.fromDate || currentDate,
            toDate: scheduleData.toDate || currentDate,
            startTime: scheduleData.startTime || currentTime,
            endTime: scheduleData.endTime || "23:59",
            link: "/"
        };

        try {
            const data = await NotificationPopupApi.create(payload);
            console.log("Notification created:", data);

            Swal.fire({
                title: "Success",
                text: "Notification popup created successfully!",
                icon: "success",
                width: "320px"
            });

            hideModalTask();

            // Reset schedule data
            setScheduleData({
                fromDate: '',
                toDate: '',
                startTime: '',
                endTime: ''
            });
            setSelectedTemplate({ title: "", description: "" });

        } catch (error) {
            console.error("Error creating notification:", error);
            Swal.fire({
                title: "Error",
                text: "Failed to create notification popup.",
                icon: "error",
                width: "320px"
            });
        }
    };

    const formatToDDMMYYYY = (dateStr) => {
        if (!dateStr) return '';

        // Already in DD-MM-YYYY? Return as is
        if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr;

        // Convert YYYY-MM-DD to DD-MM-YYYY
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (regex.test(dateStr)) {
            const [year, month, day] = dateStr.split('-');
            return `${day}-${month}-${year}`;
        }

        return dateStr; // fallback
    };

    const handleEditorChange = (val) => {
        setSelectedTemplate({ title: selectedTemplate.title, description: val });
    }
    const handleTitleChange = (e) => {
        setSelectedTemplate({ title: e.target.value, description: selectedTemplate.description });
    }

    const [inputPage, setInputPage] = useState(pageNo);

    useEffect(() => {
        const timer = setTimeout(() => {
            const value = Number(inputPage);
            setPageNo(value);
        }, 500);

        return () => clearTimeout(timer);
    }, [inputPage]);

    const [viewModel, setViewModel] = useState(false)
    const [selectedClientId, setSelectedClientId] = useState(0);
    const [currentBranchName, setCurrentBranchName] = useState('')

    const handleViewClick = (e, id, name) => {
        setViewModel(true);
        setSelectedClientId(id);
        setPriceModifier((prev) => ({
            ...prev,
            branchId: id,
        }));
        setCurrentBranchName(name);
    }

    const handleCloseViewModel = () => {
        setViewModel(false);
        setPriceModifier({
            discountCode: "",
            discountAmt: 0,
            discountDescription: "",
            additionalAmt: 0,
            additionalChargesDescription: "",
            packageDetails: "",
            totalAmt: 0,
            branchId: 0,
        })
    }

    const [priceModifier, setPriceModifier] = useState({
        discountCode: "",
        discountAmt: 0,
        discountDescription: "",
        additionalAmt: '',
        additionalChargesDescription: "",
        packageDetails: "",
        totalAmt: 0,
        branchId: 0,
        createdBy: 0,
    });

    const handleChangePriceModifier = (e) => {
        const { name, value } = e.target;

        let updatedData = { ...priceModifier };

        if (name === "discountCode") {
            const selectedCoupon = couponData?.find(
                (c) => c.CouponCode === value
            );

            updatedData.discountCode = value;
            updatedData.discountAmt = selectedCoupon?.DiscountAmt || 0;
        } else {
            updatedData[name] = value;
        }

        updatedData.totalAmt = calculateTotal(updatedData);

        setPriceModifier(updatedData);
    };

    const handlePackageChange = (value) => {
        const updatedData = {
            ...priceModifier,
            packageDetails: value,
        };

        updatedData.totalAmt = calculateTotal(updatedData);

        setPriceModifier(updatedData);
    };

    const getPackageTotal = (selectedIds) => {
        if (!selectedIds) return 0;

        const ids = selectedIds.split(",");

        return ids.reduce((sum, id) => {
            const pkg = packageData.find(p => p.id == id);
            return Number(sum) + Number(pkg?.price || 0);
        }, 0);
    };

    const calculateTotal = (data) => {
        const packageTotal = getPackageTotal(data.packageDetails);
        const discount = Number(data.discountAmt) || 0;
        const additional = Number(data.additionalAmt) || 0;

        return packageTotal - discount + additional;
    };

    console.log('priceModifier.discountCode', priceModifier.discountCode)

    const handleSavePriceModifier = async () => {
        try {
            if (Number(priceModifier.discountAmt) > 0 && (!priceModifier.discountDescription || priceModifier.discountDescription.trim() === "")) {
                alert("Please enter Coupon Remark");
                return;
            }
            if (Number(priceModifier.additionalAmt) > 0 && (!priceModifier.additionalChargesDescription || priceModifier.additionalChargesDescription.trim() === "")) {
                alert("Please enter Add On Remark");
                return;
            }
            const data = await PriceModifier.CreatePriceModifier(priceModifier);
            setPriceModifier({
                discountCode: "",
                discountAmt: 0,
                discountDescription: "",
                additionalAmt: '',
                additionalChargesDescription: "",
                packageDetails: "",
                totalAmt: 0,
            })
            fetchPriceByBranchId();
        } catch (err) {
            console.log(err);
        }
    };

    const [couponData, setCouponData] = useState([]);

    const fetchCouponData = async () => {
        const res = await CouponMaster.GetAllCoupons();
        if (res?.Result) setCouponData(res.Result);
    };

    useEffect(() => {
        fetchCouponData();
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem('authUser');
        if (stored) {
            let loginUser = JSON.parse(stored);
            setPriceModifier((prev) => ({
                ...prev,
                'createdBy': loginUser.id,
            }));
        }

    }, [viewModel])

    const [priceModifierByBranch, setPriceModifierByBranch] = useState();

    const fetchPriceByBranchId = async () => {
        try {
            const data = await PriceModifier.GetByBranchId(selectedClientId);
            setPriceModifierByBranch(data.Result);
        } catch (err) {
            console.log("err", err);
        }
    }
    useEffect(() => {
        if (selectedClientId) {
            fetchPriceByBranchId();
        }
    }, [selectedClientId])

    const convertDate = (dateStr) => {
        const date = new Date(dateStr);

        const formatted = `${String(date.getDate()).padStart(2, "0")}-${String(
            date.getMonth() + 1
        ).padStart(2, "0")}-${date.getFullYear()}`;

        return formatted;
    }

    return (
        <div className='Flex-general-client-sidebar'>
            <div className='sidebar-gen-width'>
                <SideBar />
            </div>
            <div className='Main-general-Client client-list-page'>
                <div className='header-general flex-search-general client-list-header'>
                    <h2 className='general-card-title'>&nbsp; Client List &nbsp;</h2>
                    <div className='search-main-box client-list-search'>
                        <div className="Account-select-general">
                            <select
                                className="input-select-General"
                                value={searchCriteria}
                                onChange={(e) => {
                                    setSearchCriteria(e.target.value);
                                    setPageNo(1);
                                }}
                            >
                                <option value="All">All</option>
                                <option value="FirmName">Firm Name</option>
                                <option value="BranchName">Branch Name</option>
                                <option value="UniversalLink">Universal Link</option>
                                <option value="LiveUrl">Live URL</option>
                                <option value="UrlUniqueKey">URL Unique Key</option>
                                <option value="Email">Email Id</option>
                                <option value="MobileNo">Mobile No</option>
                                {/* <option value="GSTIN">GST In</option> */}
                                <option value="State">State</option>
                                {/* <option value="ExpiryDate">Expiry Date</option> */}
                                <option value="AccountManagerName">Account Manager Name</option>
                            </select>
                        </div>
                        <div className="client-list-search-input">
                            <input
                                className="Search-input-box"
                                type="search"
                                value={searchText}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSearchText(value);
                                    setPageNo(1);
                                }}
                            />
                        </div>
                    </div>
                    <div className="client-list-actions">
                        {showGenerateTokenForAll && (
                            <button
                                className='extend-validity client-list-action-btn'
                                onClick={handleGenerateTokenForAll}
                            >
                                <u>{("Generate Token For All").toUpperCase()}</u>
                            </button>
                        )}
                        <button
                            className='extend-validity client-list-action-btn'
                            onClick={expandAllFirms}
                        >
                            <u>{("Expand All").toUpperCase()}</u>
                        </button>
                        <button
                            className='extend-validity client-list-action-btn'
                            onClick={collapseAllFirms}
                        >
                            <u>{("Collapse All").toUpperCase()}</u>
                        </button>
                        <button
                            className='extend-validity client-list-action-btn'
                            onClick={handleOpenBulkUpdateModal}
                        >
                            <u>{("Bulk Update").toUpperCase()}</u>
                        </button>
                        <button
                            className='extend-validity client-list-action-btn'
                            onClick={showModalTask}
                        >
                            <u>{("Notification Popup").toUpperCase()}</u>
                        </button>
                    </div>
                </div>
                <div className='Flex-client-box'>
                    <div className='General-card'>
                        <div className='general-card-content General-marggin-client'>
                            <div className='flex-general-client'>
                                <table className='client-table'>
                                    <thead className='header-table-search'>
                                        <tr>
                                            <th className='general-card-th'>
                                                <input
                                                    type="checkbox"
                                                    checked={allBranchesSelected}
                                                    onChange={(e) => handleSelectAllBranches(e.target.checked)}
                                                />
                                            </th>
                                            <th className='general-card-th'>Sr No</th>
                                            <th className='general-card-th'>Branch</th>
                                            <th className='general-card-th'>Email</th>
                                            <th className='general-card-th'>Address</th>
                                            <th className='general-card-th'>City</th>
                                            <th className='general-card-th'>State</th>
                                            <th className='general-card-th'>Country</th>
                                            <th className='general-card-th'>Installation</th>
                                            <th className='general-card-th'>Expiry</th>
                                            <th className='general-card-th'>Action</th>
                                            <th className='general-card-th'>Status</th>
                                            {/* <th className='general-card-th'>Remarks</th> */}
                                            <th className='general-card-th col-account-manager' style={{ width: "1%" }}>Account Manager</th>
                                            <th className='general-card-th col-package'>Packages</th>
                                            <th className='general-card-th col-business-type'>Business Type</th>
                                            <th className='general-card-th' style={{ width: "190px", maxWidth: "190px" }}>Token</th>
                                            <th className='general-card-th'>Universal Link</th>
                                            <th className='general-card-th'>Live URL</th>
                                            <th className='general-card-th'>URL Unique Key</th>
                                            <th className='general-card-th'></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.keys(groupedClients).map((firmName) => {
                                            const firmBranches = groupedClients[firmName];
                                            const branchCount = firmBranches.length;
                                            const isFirmCollapsed = !!collapsedFirms[firmName];
                                            const firstBranch = firmBranches[0];
                                            const firmRegisterClient = getRegisterClientForBranch(firstBranch);
                                            const firmEmail = firmRegisterClient?.Email || firstBranch?.emailId || '';
                                            const registerClientId = firmRegisterClient?.id;
                                            const f3Value = registerClientId != null && firmF3Values[registerClientId] !== undefined
                                                ? firmF3Values[registerClientId]
                                                : (firmRegisterClient?.F3 ?? '');
                                            const firmAccountManagerValue =
                                                firmAccountManagerEdits[firmName] !== undefined
                                                    ? firmAccountManagerEdits[firmName]
                                                    : getFirmAccountManagerId(firmBranches);
                                            const isSavingFirmManager = !!savingFirmAccountManager[firmName];
                                            const firmBranchIds = firmBranches.map((branch) => branch.id);
                                            const allFirmBranchesSelected =
                                                firmBranchIds.length > 0 &&
                                                firmBranchIds.every((id) => selectedBranchIds.includes(id));
                                            const someFirmBranchesSelected =
                                                !allFirmBranchesSelected &&
                                                firmBranchIds.some((id) => selectedBranchIds.includes(id));
                                            const firmInstallationValue =
                                                firmInstallationEdits[firmName] !== undefined
                                                    ? firmInstallationEdits[firmName]
                                                    : getFirmCommonDateValue(firmBranches, ['InstallationDate', 'installationDate']);
                                            const firmExpiryValue =
                                                firmExpiryEdits[firmName] !== undefined
                                                    ? firmExpiryEdits[firmName]
                                                    : getFirmCommonDateValue(firmBranches, ['ExpiryDate', 'expiryDate']);
                                            const firmStatusValue = getFirmCommonStatusValue(firmBranches);
                                            const firmIsInactive = firmStatusValue === 'inactive';
                                            const isSavingFirmInstall = !!savingFirmInstallation[firmName];
                                            const isSavingFirmExpiryDate = !!savingFirmExpiry[firmName];
                                            const isSavingFirmStatusValue = !!savingFirmStatus[firmName];

                                            return (
                                                <Fragment key={`group-${firmName}`}>
                                                    <tr key={`header-${firmName}`}>
                                                        <td
                                                            className='firm-name-heading'
                                                            style={{ textAlign: 'center', padding: "10px", verticalAlign: "middle", width: "50px" }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                title="Select all branches of this company"
                                                                checked={allFirmBranchesSelected}
                                                                ref={(el) => {
                                                                    if (el) el.indeterminate = someFirmBranchesSelected;
                                                                }}
                                                                onChange={(e) => handleSelectFirmBranches(firmBranches, e.target.checked)}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </td>
                                                        <td
                                                            className='firm-name-heading'
                                                            colSpan={7}
                                                            style={{ textAlign: 'left', fontWeight: 700, padding: "15px", cursor: "pointer" }}
                                                            onClick={() => toggleFirmCollapse(firmName)}
                                                        >
                                                            <div className="firm-name-heading-left">
                                                                <Icon
                                                                    icon={isFirmCollapsed ? "lucide:chevron-right" : "lucide:chevron-down"}
                                                                    width={18}
                                                                    height={18}
                                                                />
                                                                <span className="firm-name-heading-text">{firmName}</span>
                                                                {firmEmail ? (
                                                                    <span className="firm-name-heading-email">{` (${firmEmail})`}</span>
                                                                ) : null}
                                                                <span className="firm-branch-count">
                                                                    {`(${branchCount} ${branchCount === 1 ? 'Branch' : 'Branches'})`}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td
                                                            className='firm-name-heading firm-header-field-cell'
                                                            style={{ padding: "8px 6px", verticalAlign: "middle" }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <span className="firm-header-field-controls">
                                                                <input
                                                                    type="date"
                                                                    className="firm-header-date-input"
                                                                    title="Installation date for all branches"
                                                                    value={firmInstallationValue}
                                                                    onChange={(e) =>
                                                                        setFirmInstallationEdits((prev) => ({
                                                                            ...prev,
                                                                            [firmName]: e.target.value,
                                                                        }))
                                                                    }
                                                                    disabled={isSavingFirmInstall}
                                                                />
                                                                <Icon
                                                                    icon="ion:save"
                                                                    className='save-icon-btn'
                                                                    title="Save Installation for all branches"
                                                                    onClick={(e) => handleSaveFirmInstallation(e, firmName, firmBranches)}
                                                                    style={{
                                                                        opacity: isSavingFirmInstall ? 0.5 : 1,
                                                                        pointerEvents: isSavingFirmInstall ? "none" : "auto",
                                                                    }}
                                                                />
                                                            </span>
                                                        </td>
                                                        <td
                                                            className='firm-name-heading firm-header-field-cell'
                                                            style={{ padding: "8px 6px", verticalAlign: "middle" }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <span className="firm-header-field-controls">
                                                                <input
                                                                    type="date"
                                                                    className="firm-header-date-input"
                                                                    title="Expiry date for all branches"
                                                                    value={firmExpiryValue}
                                                                    onChange={(e) =>
                                                                        setFirmExpiryEdits((prev) => ({
                                                                            ...prev,
                                                                            [firmName]: e.target.value,
                                                                        }))
                                                                    }
                                                                    disabled={isSavingFirmExpiryDate}
                                                                />
                                                                <Icon
                                                                    icon="ion:save"
                                                                    className='save-icon-btn'
                                                                    title="Save Expiry for all branches"
                                                                    onClick={(e) => handleSaveFirmExpiry(e, firmName, firmBranches)}
                                                                    style={{
                                                                        opacity: isSavingFirmExpiryDate ? 0.5 : 1,
                                                                        pointerEvents: isSavingFirmExpiryDate ? "none" : "auto",
                                                                    }}
                                                                />
                                                            </span>
                                                        </td>
                                                        <td className='firm-name-heading' style={{ padding: "8px" }} />
                                                        <td
                                                            className='firm-name-heading firm-header-field-cell'
                                                            style={{ padding: "8px 6px", verticalAlign: "middle" }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    cursor: isSavingFirmStatusValue ? "not-allowed" : "pointer",
                                                                    opacity: isSavingFirmStatusValue ? 0.5 : 1,
                                                                    gap: "4px",
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {firmStatusValue === 'inactive'
                                                                    ? 'Inactive'
                                                                    : firmStatusValue === 'active'
                                                                        ? 'Active'
                                                                        : 'Active'}
                                                                {firmIsInactive ? (
                                                                    <Icon
                                                                        icon="material-symbols:not-started-rounded"
                                                                        width="20"
                                                                        height="20"
                                                                        title="Set all branches Active"
                                                                        onClick={(e) => {
                                                                            if (isSavingFirmStatusValue) return;
                                                                            handleSaveFirmStatus(e, firmName, firmBranches, 'active');
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Icon
                                                                        icon="material-symbols:stop-circle"
                                                                        width="20"
                                                                        height="20"
                                                                        title="Set all branches Inactive"
                                                                        onClick={(e) => {
                                                                            if (isSavingFirmStatusValue) return;
                                                                            handleSaveFirmStatus(e, firmName, firmBranches, 'inactive');
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td
                                                            className='firm-name-heading firm-account-manager-cell col-account-manager'
                                                            style={{ padding: "8px 10px", verticalAlign: "middle" }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <span className="firm-account-manager-controls">
                                                                <select
                                                                    className="firm-account-manager-select"
                                                                    title="Account Manager for all branches"
                                                                    value={firmAccountManagerValue}
                                                                    onChange={(e) =>
                                                                        setFirmAccountManagerEdits((prev) => ({
                                                                            ...prev,
                                                                            [firmName]: e.target.value,
                                                                        }))
                                                                    }
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    disabled={isSavingFirmManager}
                                                                >
                                                                    <option value="">Account Manager</option>
                                                                    {accountManagerMasterData.map((manager) => (
                                                                        <option key={manager.id} value={manager.id}>
                                                                            {manager.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <Icon
                                                                    icon="ion:save"
                                                                    className='save-icon-btn'
                                                                    title="Save Account Manager for all branches"
                                                                    onClick={(e) => handleSaveFirmAccountManager(e, firmName, firmBranches)}
                                                                    style={{
                                                                        opacity: isSavingFirmManager ? 0.5 : 1,
                                                                        pointerEvents: isSavingFirmManager ? "none" : "auto",
                                                                    }}
                                                                />
                                                            </span>
                                                        </td>
                                                        <td
                                                            className='firm-name-heading'
                                                            colSpan={7}
                                                            style={{ textAlign: 'right', fontWeight: 700, padding: "15px" }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {registerClientId != null && (
                                                                <span className="firm-f3-controls">
                                                                    <input
                                                                        type="number"
                                                                        className="firm-f3-input"
                                                                        placeholder="Item Master Limit"
                                                                        title="Item Master Limit"
                                                                        value={f3Value}
                                                                        onChange={(e) => setFirmF3Values((prev) => ({
                                                                            ...prev,
                                                                            [registerClientId]: e.target.value
                                                                        }))}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        style={{ height: "26px" }}
                                                                    />
                                                                    <Icon
                                                                        icon="ion:save"
                                                                        className='save-icon-btn'
                                                                        title="Save Item Master Limit"
                                                                        onClick={(e) => handleSaveFirmF3(e, firmRegisterClient)}
                                                                        style={{
                                                                            opacity: savingF3Ids[registerClientId] ? 0.5 : 1,
                                                                            pointerEvents: savingF3Ids[registerClientId] ? "none" : "auto",
                                                                        }}
                                                                    />
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {!isFirmCollapsed && firmBranches.map((client, branchIndex) => {
                                                        // const accountManagerInfo = accountManagerInformationData.find(
                                                        //     (info) => info.branchId === client.id && info.active
                                                        // );

                                                        // const accountManagerName = accountManagerInfo
                                                        //     ? accountManagerMasterData.find((manager) => manager.id === accountManagerInfo.acManagerId)?.name
                                                        //     : "No Manager Assigned";

                                                        const accountManagerInfo = accountManagerInformationData.find(
                                                            (info) => info.branchId === client.id && info.active
                                                        );

                                                        const accountManagerId = accountManagerInfo ? accountManagerInfo.acManagerId : "";

                                                        const currentPackageId = client.packageId;
                                                        // const currentPackageName = packageData.find(pkg => pkg.id === currentPackageId)?.name || "Unknown Package";
                                                        // const isDropdownOpen = selectedDropdown.firmName === firmName && selectedDropdown.branchIndex === branchIndex;
                                                        const toggleStatus = () => {
                                                            const updatedStatus = !client.StopWorking;
                                                            handleServiceExpiryDateSave(client.id, updatedStatus, client)
                                                        };

                                                        const handleManagerChange = async (newManagerId) => {
                                                            const confirmChange = await Swal.fire({
                                                                title: "Are you sure?",
                                                                text: "Are you sure you want to change the account manager?",
                                                                showCancelButton: true,
                                                                confirmButtonText: "Yes, change",
                                                                cancelButtonText: "Cancel",
                                                                width: "340px"
                                                            });

                                                            if (confirmChange.isConfirmed) {
                                                                handleAccountManagerChange(client.id, newManagerId, client);
                                                            } else {
                                                                console.log("Account manager change cancelled");
                                                            }
                                                        };

                                                        const handlePackageChange = async (newPackageId) => {
                                                            const confirmChange = await Swal.fire({
                                                                title: "Are you sure?",
                                                                text: "Are you sure you want to change the package?",
                                                                showCancelButton: true,
                                                                confirmButtonText: "Yes, change",
                                                                cancelButtonText: "Cancel",
                                                                width: "340px"
                                                            });

                                                            if (confirmChange.isConfirmed) {
                                                                handlePackageChange1(client.id, newPackageId, client);
                                                            } else {
                                                                console.log("Package change cancelled");
                                                            }
                                                        };
                                                        // console.log("client", client);
                                                        const selectedBusinessIds = clientSelectedOptions[client.id] || [];
                                                        const selectedBusinessNames = selectedBusinessIds
                                                            .map((businessId) =>
                                                                businessData.find(
                                                                    (option) => String(option.id) === String(businessId)
                                                                )?.businessName
                                                            )
                                                            .filter(Boolean);
                                                        const displayValue = selectedBusinessNames.length > 0
                                                            ? selectedBusinessNames.join(", ")
                                                            : "Select Business Type";

                                                        return (
                                                            <tr className={`client-row 
                                                            ${selectedBranchIds.includes(client.id) ? 'selected' : ''}`
                                                            }
                                                                key={`${firmName}-${client.id || branchIndex}`} style={{ backgroundColor: client.StopWorking !== true ? '' : '#cf00002b' }} onClick={closeAllDropdowns}> {/* #03be0363 */}
                                                                <td className='general-card-detail'>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedBranchIds.includes(client.id)}
                                                                        onChange={(e) => handleBranchSelect(client.id, e.target.checked)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </td>
                                                                <td className='general-card-detail'>{++serialNo}</td>
                                                                <td className='general-card-detail'>{client.branchName}</td>
                                                                <td className='general-card-detail'>{client.emailId}</td>
                                                                <td className='general-card-detail' style={{ minWidth: "180px", maxWidth: "220px", }}>{client.address ? client.address : "-"}</td>
                                                                <td className='general-card-detail'>{client.city ? client.city : "-"}</td>
                                                                <td className='general-card-detail'>{client.state ? client.state : "-"}</td>
                                                                <td className='general-card-detail'>{client.country ? client.country : "-"}</td>
                                                                <td className='general-card-detail' onClick={(e) => e.stopPropagation()}>
                                                                    <div className='flex-table-business' style={{ alignItems: "center", gap: "4px" }}>
                                                                        <input
                                                                            type="date"
                                                                            className="Business_im_input mode_category1"
                                                                            style={{ width: "140px", height: "32px", padding: "2px 4px" }}
                                                                            value={
                                                                                installationDateEdits[client.id] !== undefined
                                                                                    ? installationDateEdits[client.id]
                                                                                    : toDateInputValue(client.InstallationDate)
                                                                            }
                                                                            onChange={(e) =>
                                                                                setInstallationDateEdits((prev) => ({
                                                                                    ...prev,
                                                                                    [client.id]: e.target.value,
                                                                                }))
                                                                            }
                                                                            disabled={!!savingInstallationIds[client.id]}
                                                                        />
                                                                        <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                            <Icon
                                                                                icon="ion:save"
                                                                                className='save-icon-btn'
                                                                                onClick={(e) => handleInstallationDateSave(e, client)}
                                                                                style={{
                                                                                    opacity: savingInstallationIds[client.id] ? 0.5 : 1,
                                                                                    pointerEvents: savingInstallationIds[client.id] ? "none" : "auto",
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className='general-card-detail' style={{ minWidth: "110px" }}>{client.ExpiryDate ? convertToGMT530(client.ExpiryDate) : '----'}</td>
                                                                <td>
                                                                    <button className='extend-validity' style={{ padding: "2px 5px" }} onClick={() => showModalValidity(client)}>Validity</button>
                                                                </td>
                                                                <td
                                                                    // style={{ color: client.StopWorking !== true ? 'green' : 'red' }} 
                                                                    className='general-card-detail' >
                                                                    <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                        {client.StopWorking !== true ? 'Active' : 'Inactive'}
                                                                        {client.StopWorking ? (
                                                                            <Icon icon="material-symbols:not-started-rounded" width="20" height="20" onClick={toggleStatus} />
                                                                        ) : (
                                                                            <Icon icon="material-symbols:stop-circle" width="20" height="20" onClick={toggleStatus} />
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                {/* <td className='general-card-detail'>{client.remarks}</td> */}
                                                                <td className='general-card-detail col-account-manager' style={{ padding: "2px 15px" }}>
                                                                    <div className="input-box-mok1">
                                                                        <SearchableDropdown
                                                                            options={accountManagerMasterData}
                                                                            value={accountManagerId}
                                                                            onChange={handleManagerChange}
                                                                            labelField="name"
                                                                            valueField="id"
                                                                            placeholder="Account Manager"
                                                                            required
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td className='general-card-detail col-package'>
                                                                    <div className="input-box-mok1">
                                                                        <SearchableDropdown
                                                                            options={packageData}
                                                                            value={currentPackageId}
                                                                            onChange={handlePackageChange}
                                                                            labelField="name"
                                                                            valueField="id"
                                                                            placeholder="Type Package"
                                                                            required
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td className='general-card-detail col-business-type' onClick={(e) => e.stopPropagation()}>
                                                                    <div className='flex-table-business'>
                                                                        {/* <div>{client.businessType}</div> */}
                                                                        <div className='flex-businesstype'>
                                                                            <div className="Business-general-card">
                                                                                <div className="input-box-mok1"
                                                                                    onClick={() => toggleDropdown(client.id)}
                                                                                // onClick={() => toggleDropdown(firmName, branchIndex, client.id)}
                                                                                >
                                                                                    <select value={client.businessType || ''} style={{ width: "240px" }}
                                                                                        className="Business_im_input mode_category1" required>
                                                                                        <option>{displayValue}</option>
                                                                                    </select>
                                                                                    <div className="overSelect"></div>
                                                                                </div>
                                                                                {openDropdowns[client.id] && (
                                                                                    <div id="checkBoxes" className="dropdown-content">
                                                                                        <label className='business-options' htmlFor="selectAll">
                                                                                            <input
                                                                                                className="user_checkbox"
                                                                                                type="checkbox"
                                                                                                id="selectAll"
                                                                                                // checked={selectedOptions.length === businessData.length}
                                                                                                checked={clientSelectedOptions[client.id]?.length === businessData.length}
                                                                                                onChange={(e) => handleSelectAllChange(client.id, e)}
                                                                                            />
                                                                                            Select All
                                                                                        </label>
                                                                                        <hr />
                                                                                        {businessData.map(option => (
                                                                                            <>
                                                                                                <div key={option.id} style={{ display: 'flex', flexDirection: 'row' }}>
                                                                                                    <label key={option.id} className='business-options' htmlFor={option.id}>
                                                                                                        <input
                                                                                                            className="user_checkbox"
                                                                                                            type="checkbox"
                                                                                                            id={option.id}
                                                                                                            // checked={selectedOptions.includes(option.id)}
                                                                                                            // onChange={() => handleCheckboxChange(option.id)}
                                                                                                            // checked={selectedOptions.includes(option.businessName)}
                                                                                                            checked={clientSelectedOptions[client.id]?.includes(String(option.id))}
                                                                                                            onChange={() => handleCheckboxChange(client.id, String(option.id))}
                                                                                                        />
                                                                                                        {option.businessName}
                                                                                                    </label>
                                                                                                </div>
                                                                                                <hr />
                                                                                            </>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                                <Icon icon="ion:save" className='save-icon-btn' onClick={(e) => handleBusinessTypeSave(e, client.id, client)} />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td
                                                                    className='general-card-detail'
                                                                    style={{ width: "190px", maxWidth: "190px" }}
                                                                >
                                                                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                        <span
                                                                            style={{
                                                                                flex: 1,
                                                                                minWidth: 0,
                                                                                whiteSpace: "nowrap",
                                                                                overflow: "hidden",
                                                                                textOverflow: "ellipsis"
                                                                            }}
                                                                            title={getBranchFieldValue(client, ['token', 'Token'])}
                                                                        >
                                                                            {getBranchFieldValue(client, ['token', 'Token'])}
                                                                        </span>
                                                                        {(() => {
                                                                            const tokenValue = getBranchFieldValue(client, ['token', 'Token']);
                                                                            const canCopy = tokenValue && tokenValue !== '-';
                                                                            return canCopy ? (
                                                                                <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                                                                                    <button
                                                                                        className='extend-validity'
                                                                                        style={{ padding: "2px 6px", backgroundColor: "grey", border: "1px solid darkgrey" }}
                                                                                        onClick={(e) => handleCopyToken(e, tokenValue, client.id)}
                                                                                    >
                                                                                        Copy
                                                                                    </button>
                                                                                    {copiedTokenRowId === client.id && (
                                                                                        <span
                                                                                            style={{
                                                                                                position: "absolute",
                                                                                                top: "-24px",
                                                                                                right: "0",
                                                                                                background: "#d1d1d1",
                                                                                                color: "black",
                                                                                                padding: "2px 6px",
                                                                                                borderRadius: "4px",
                                                                                                fontSize: "11px",
                                                                                                whiteSpace: "nowrap",
                                                                                                border: "1px solid black"
                                                                                            }}
                                                                                        >
                                                                                            Copied
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            ) : null;
                                                                        })()}
                                                                    </div>
                                                                </td>
                                                                <td className='general-card-detail'>{getBranchFieldValue(client, ['universalLink', 'UniversalLink'])}</td>
                                                                <td className='general-card-detail'>{getBranchFieldValue(client, ['liveUrl', 'liveURL', 'LiveUrl', 'LiveURL'])}</td>
                                                                <td className='general-card-detail'>{getBranchFieldValue(client, ['urlUniqueKey', 'UrlUniqueKey', 'urluniquekey', 'URLUniqueKey'])}</td>
                                                                <td>
                                                                    <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                                        <span
                                                                            style={{ position: "relative", display: "inline-flex", alignContent: "center", height: "50px" }}
                                                                            onMouseEnter={(e) => {
                                                                                e.currentTarget.querySelector(".view-tooltip").style.visibility = "visible";
                                                                                e.currentTarget.querySelector(".view-tooltip").style.opacity = "1";
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                e.currentTarget.querySelector(".view-tooltip").style.visibility = "hidden";
                                                                                e.currentTarget.querySelector(".view-tooltip").style.opacity = "0";
                                                                            }}
                                                                        >
                                                                            <Icon
                                                                                icon="ion:eye"
                                                                                className="save-icon-btn"
                                                                                style={{
                                                                                    color: "black",
                                                                                    fontSize: "26px",
                                                                                    margin: "10px"
                                                                                }}
                                                                                onClick={(e) =>
                                                                                    handleViewClick(e, client.id, client.branchName)
                                                                                }
                                                                            />

                                                                            <span
                                                                                className="view-tooltip"
                                                                                style={{
                                                                                    visibility: "hidden",
                                                                                    opacity: 0,
                                                                                    position: "absolute",
                                                                                    bottom: "100%",
                                                                                    left: "50%",
                                                                                    transform: "translateX(-50%)",
                                                                                    backgroundColor: "#333",
                                                                                    color: "#fff",
                                                                                    padding: "10px 15px",
                                                                                    borderRadius: "4px",
                                                                                    fontSize: "12px",
                                                                                    whiteSpace: "nowrap",
                                                                                    transition: "opacity 0.2s ease",
                                                                                    zIndex: 1000,
                                                                                    pointerEvents: "none",
                                                                                    fontWeight: 600
                                                                                }}
                                                                            >
                                                                                ADD/VIEW COUPON
                                                                            </span>
                                                                        </span>
                                                                        <button
                                                                            className='extend-validity'
                                                                            style={{ padding: "5px 5px", marginRight: "6px" }}
                                                                            onClick={(e) => handleGenerateToken(e, client)}
                                                                        >
                                                                            Generate Token
                                                                        </button>
                                                                        <Icon icon="ion:trash" className='save-icon-btn' style={{ color: "red", fontSize: "24px" }} title={'Delete Branch'} onClick={(e) => handleDeleteClient(e, client.id)} />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )
                                                    }
                                                    )}
                                                </Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {isModalOpenValidity && selectedClient && (
                                    <div className="modal">
                                        <div className="OpticalField-modal Report-template-model" style={{ height: "auto" }}>
                                            <div className="modal-title-Report" style={{ padding: "10px" }}>
                                                <h3 className="Report-Fixed-Header">Extend Validity for {(selectedClient?.branchName).toUpperCase()}</h3>
                                                <span className="close_modal" onClick={hideModalValidity}>&times;</span>
                                            </div>
                                            <div className="tab-content-modal">
                                                <div className="company-info-top-modal control_flex_modal item_scroll">
                                                    <ModelValidityExtend
                                                        clientData={selectedClient}
                                                        onSaveSuccess={handleValiditySaveSuccess}
                                                        handleGenerateToken={handleGenerateToken}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {/* ***************************** Bulk Update ******************************** */}
                                {isBulkUpdateModalOpen && (
                                    <div className="modal">
                                        <div className="OpticalField-modal Report-template-model" style={{ height: "auto", maxWidth: "370px" }}>
                                            <div className="modal-title-Report" style={{ padding: "10px" }}>
                                                <h3 className="Report-Fixed-Header">Bulk Update Selected Branches</h3>
                                                <span className="close_modal" onClick={() => setIsBulkUpdateModalOpen(false)}>&times;</span>
                                            </div>
                                            <div style={{ display: "flex", justifyContent: "space-evenly", flexWrap: "wrap", gap: "8px" }}>
                                                <div style={{ marginBottom: "10px" }}>
                                                    <label style={{ fontWeight: 600, marginBottom: "6px" }}>Field</label>
                                                    <select
                                                        className="Business_im_input mode_category1"
                                                        value={bulkUpdateField}
                                                        onChange={(e) => {
                                                            setBulkUpdateField(e.target.value);
                                                            setBulkUpdateText('');
                                                            setBulkAccountManagerId('');
                                                        }}
                                                        style={{ height: "45px" }}
                                                    >
                                                        <option value="universalLink">Universal Link</option>
                                                        <option value="urlUniqueKey">URL Unique Key</option>
                                                        <option value="liveUrl">Live URL</option>
                                                        <option value="accountManagerId">Account Manager</option>
                                                    </select>
                                                </div>
                                                <div style={{ marginBottom: "12px" }}>
                                                    <label style={{ fontWeight: 600, display: "block", marginBottom: "6px" }}>
                                                        {bulkUpdateField === 'accountManagerId' ? 'Account Manager' : 'Text'}
                                                    </label>
                                                    {bulkUpdateField === 'accountManagerId' ? (
                                                        <select
                                                            className="Business_im_input mode_category1"
                                                            value={bulkAccountManagerId}
                                                            onChange={(e) => setBulkAccountManagerId(e.target.value)}
                                                            style={{ height: "45px", minWidth: "180px" }}
                                                        >
                                                            <option value="">----Select----</option>
                                                            {accountManagerMasterData.map((manager) => (
                                                                <option key={manager.id} value={manager.id}>
                                                                    {manager.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <textarea
                                                            className="Business_im_input mode_category1"
                                                            rows={3}
                                                            value={bulkUpdateText}
                                                            onChange={(e) => setBulkUpdateText(e.target.value)}
                                                            placeholder="Enter text to apply to selected branches"
                                                            style={{ height: "45px" }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div style={{ margin: "10px" }}>
                                                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                                                    <button
                                                        className='extend-validity'
                                                        style={{ backgroundColor: "#777", padding: "10px" }}
                                                        onClick={() => setIsBulkUpdateModalOpen(false)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button style={{ padding: "10px" }}
                                                        className='extend-validity'
                                                        onClick={handleBulkUpdateSelectedBranches}
                                                    >
                                                        Update Selected
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="client-list-pagination" style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '10px' }}>
                    <button style={{ padding: "5px" }}
                        className='extend-validity'
                        onClick={() => {
                            setPageNo((prev) => {
                                const newPage = Math.max(prev - 1, 1);
                                setInputPage(newPage);
                                return newPage;
                            });
                        }}
                        disabled={pageNo === 1}
                    >
                        Prev
                    </button>
                    <span>Page&nbsp;
                        <input className="pageNoInput" type="text"
                            value={inputPage}
                            onChange={(e) => setInputPage(e.target.value)} />
                        &nbsp;of {totalPages}</span>
                    <button style={{ padding: "5px" }}
                        className='extend-validity'
                        onClick={() => {
                            setPageNo((prev) => {
                                const newPage = prev < totalPages ? prev + 1 : prev;
                                setInputPage(newPage);
                                return newPage;
                            });
                        }}
                        disabled={pageNo >= totalPages}
                    >
                        Next
                    </button>
                </div>
            </div>

            {/* ********************* Notification Popup ********************** */}
            {isModalOpenTask && (
                <div className="modal-task">
                    <div className="OpticalField-modal Remark-List-model"
                        style={{ height: "auto", maxWidth: "800px", maxHeight: "815px" }}>

                        <div className="modal-title-Report">
                            <h3 className="Report-Fixed-Header">Schedule Details</h3>
                            <span className="close_modal" onClick={hideModalTask}>&times;</span>
                        </div>

                        <div className="tab-content-modal">
                            <div className="company-info-top-modal control_flex_modal item_scroll">
                                <div style={{}}>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>

                                        {/* From Date */}
                                        <div style={{ position: "relative" }}>
                                            <label style={{ fontWeight: 600, display: "block", marginBottom: "6px" }}>
                                                From Date
                                            </label>
                                            <input
                                                type="text"
                                                className="padding_18 Business_im_input mode_category1"
                                                value={formatToDDMMYYYY(scheduleData.fromDate)}
                                                readOnly
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => e.target.nextSibling.showPicker()}   // Opens date picker on click
                                            />
                                            <input
                                                type="date"
                                                className="padding_18 Business_im_input mode_category1"
                                                value={scheduleData.fromDate || ''}
                                                onChange={(e) => setScheduleData(prev => ({
                                                    ...prev,
                                                    fromDate: e.target.value
                                                }))}
                                                style={{
                                                    position: "absolute",
                                                    top: "37px",
                                                    left: "0",
                                                    width: "100%",
                                                    height: "48px",
                                                    opacity: 0,
                                                    pointerEvents: "none"   // Important: prevents double triggering
                                                }}
                                            />
                                        </div>

                                        {/* To Date */}
                                        <div style={{ position: "relative" }}>
                                            <label style={{ fontWeight: 600, display: "block", marginBottom: "6px" }}>
                                                To Date
                                            </label>
                                            <input
                                                type="text"
                                                className="padding_18 Business_im_input mode_category1"
                                                value={formatToDDMMYYYY(scheduleData.toDate)}
                                                readOnly
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => e.target.nextSibling.showPicker()}
                                            />
                                            <input
                                                type="date"
                                                className="padding_18 Business_im_input mode_category1"
                                                value={scheduleData.toDate || ''}
                                                onChange={(e) => setScheduleData(prev => ({
                                                    ...prev,
                                                    toDate: e.target.value
                                                }))}
                                                style={{
                                                    position: "absolute",
                                                    top: "37px",
                                                    left: "0",
                                                    width: "100%",
                                                    height: "48px",
                                                    opacity: 0,
                                                    pointerEvents: "none"
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "12px" }}>
                                        <div>
                                            <label style={{ fontWeight: 600, display: "block", marginBottom: "6px" }}>
                                                Start Time
                                            </label>
                                            <input
                                                type="time"
                                                className="padding_18 Business_im_input mode_category1"
                                                value={scheduleData.startTime}
                                                onChange={(e) => setScheduleData(prev => ({ ...prev, startTime: e.target.value }))}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontWeight: 600, display: "block", marginBottom: "6px" }}>
                                                End Time
                                            </label>
                                            <input
                                                type="time"
                                                className="padding_18 Business_im_input mode_category1"
                                                value={scheduleData.endTime}
                                                onChange={(e) => setScheduleData(prev => ({ ...prev, endTime: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div style={{ marginTop: "10px" }}>
                                    <label style={{ fontWeight: 600, display: "block", marginBottom: "6px" }}>
                                        Title
                                    </label>
                                    <input
                                        type="text"
                                        className="padding_18 Business_im_input mode_category1"
                                        value={selectedTemplate.title}
                                        onChange={(e) => handleTitleChange(e)}
                                    />
                                </div>
                                <div style={{ paddingTop: "20px" }}>
                                    <JoditEditor onChange={handleEditorChange} value={selectedTemplate.description} />
                                </div>
                                {/* Template Selection */}
                                <label style={{ fontWeight: 600, display: "block", marginBottom: "6px", marginTop: "10px" }}>Select Template</label>
                                <ModelssueRemarkList
                                    onClose={hideModalTask}
                                    onUpdate={handleRemarkUpdate}
                                    setTemplateData={setSelectedTemplate}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* ************************** View Model *************************** */}
            {viewModel && (
                <div className="modal-task">
                    <div className="OpticalField-modal Remark-List-model"
                        style={{ height: "auto", maxWidth: "800px", maxHeight: "815px", width: "90%" }}>

                        <div className="modal-title-Report">
                            <h3 className="Report-Fixed-Header">View Coupon Details - {currentBranchName}</h3>
                            <span className="close_modal" onClick={handleCloseViewModel}>&times;</span>
                        </div>

                        <div className="tab-content-modal">
                            <div className="company-info-top-modal control_flex_modal item_scroll">
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                                    <div style={{ position: "relative" }}>
                                        <label style={{ fontSize: "14px" }}>Add Coupon</label>
                                        <div className="input_group_combo" style={{ display: "flex" }}>
                                            <select style={{ height: "38px", padding: "2px" }}
                                                className="padding_18 Business_im_input mode_category1 left_select"
                                                name="discountCode"
                                                value={priceModifier.discountCode}
                                                onChange={(e) => handleChangePriceModifier(e)}
                                            >
                                                <option value="">Coupon Code</option>
                                                {couponData?.map((data, i) => (
                                                    <option key={i} value={data.CouponCode}>
                                                        {data.CouponCode}
                                                    </option>
                                                ))}
                                            </select>

                                            <input style={{ height: "38px", padding: "2px", width: "55px" }}
                                                type="text"
                                                disabled
                                                className="padding_18 Business_im_input mode_category1 right_input"
                                                placeholder="Enter value"
                                                value={priceModifier.discountAmt}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ position: "relative" }}>
                                        <label style={{ fontSize: "14px" }}>Coupon Remark</label>
                                        <input
                                            type="text"
                                            className="padding_18 Business_im_input mode_category1"
                                            name="discountDescription"
                                            onChange={(e) => handleChangePriceModifier(e)}
                                            value={priceModifier.discountDescription}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                                    <div style={{ position: "relative" }}>
                                        <label style={{ fontSize: "14px" }}>Add Charges</label>
                                        <input
                                            type="number"
                                            className="padding_18 Business_im_input mode_category1"
                                            name="additionalAmt"
                                            onChange={(e) => handleChangePriceModifier(e)}
                                            value={priceModifier.additionalAmt}
                                        />
                                    </div>

                                    <div style={{ position: "relative" }}>
                                        <label style={{ fontSize: "14px" }}>Add On Remark</label>
                                        <input
                                            type="text"
                                            className="padding_18 Business_im_input mode_category1"
                                            name="additionalChargesDescription"
                                            onChange={(e) => handleChangePriceModifier(e)}
                                            value={priceModifier.additionalChargesDescription}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: "flex", marginBottom: "12px", justifyContent: "space-between" }}>
                                    <div className="input-box-mok1" style={{ width: "50%" }}>
                                        <label style={{ fontSize: "14px" }}>Packages</label>
                                        <MultiSelectDropdown
                                            options={packageData}
                                            value={priceModifier.packageDetails}
                                            onChange={handlePackageChange}
                                        />
                                    </div>
                                    <div className="input-box-mok1" style={{ width: "50%", display: "flex", justifyContent: "space-between", alignItems: "center", marginLeft: "10px" }}>
                                        <div style={{ position: "relative", width: "60%" }}>
                                            <label style={{ fontSize: "14px" }}>Total Amount</label>
                                            <input disabled
                                                type="text"
                                                className="padding_18 Business_im_input mode_category1"
                                                value={`₹ ${priceModifier.totalAmt}`}
                                            />
                                        </div>
                                        <button className="temp-save" style={{ padding: "0px", height: "40px", marginTop: "20px" }} onClick={handleSavePriceModifier}>Save</button>
                                    </div>
                                </div>
                                <hr />
                                <div>
                                    <label style={{ fontWeight: "bold", textDecoration: "underline" }}>Price Modifier :-</label>
                                    <div className="control_radius">
                                        <div className="table-account-manager">
                                            <table className="table-master-acc">
                                                {/* ---------------- HEADER ---------------- */}
                                                <thead className="header-table-account">
                                                    <tr>
                                                        <th className="table-header-manager ac-sno-wth">S.No</th>
                                                        <th className="table-header-manager">Coupon Code</th>
                                                        <th className="table-header-manager">Coupon Amt</th>
                                                        <th className="table-header-manager">Coupon Description</th>
                                                        <th className="table-header-manager">Add On Amt</th>
                                                        <th className="table-header-manager">Add On Description</th>
                                                        <th className="table-header-manager">Packages</th>
                                                        <th className="table-header-manager">Total Amt</th>
                                                        <th className="table-header-manager">Given By</th>
                                                        <th className="table-header-manager">Date</th>
                                                        <th className="table-header-manager">Paid / Not Paid</th>
                                                    </tr>
                                                </thead>

                                                {/* ---------------- BODY ---------------- */}
                                                <tbody>
                                                    {priceModifierByBranch?.map((item, idx) => (
                                                        console.log("priceItem", item),
                                                        <tr key={item.id}>

                                                            <td className="table-data-account ac-sno-wth">
                                                                {idx + 1}
                                                            </td>

                                                            <td className="table-data-account">
                                                                {item.discountCode}
                                                            </td>

                                                            <td className="table-data-account">
                                                                {item.discountAmt}
                                                            </td>

                                                            <td className="table-data-account">
                                                                {item.discountDescription}
                                                            </td>

                                                            <td className="table-data-account">
                                                                {item.additionalAmt}
                                                            </td>

                                                            <td className="table-data-account">
                                                                {item.additionalChargesDescription}
                                                            </td>

                                                            <td className="table-data-account">
                                                                {item.packageNames}
                                                            </td>

                                                            <td className="table-data-account">
                                                                ₹{item.totalAmt}
                                                            </td>

                                                            <td className="table-data-account">
                                                                {item.createdByName}
                                                            </td>

                                                            <td className="table-data-account">
                                                                {convertDate(item.createdOn)}
                                                            </td>

                                                            <td className="table-data-account">
                                                                {item.isUsed ? "Paid" : "Not Paid"}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>

                                            </table>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {loading && <Loader />}
        </div >
    )
}

export default ClientList
